/**
 * DeviceDetail — Device Center v3 (Sprint 6.5B, cockpit operacional).
 *
 * Cockpit completo de operação de um dispositivo. Consome o
 * endpoint agregado /api/admin/devices/{id}/center e o organiza
 * em 8 blocos: header cockpit, grid de comandos, telemetria live,
 * timeline operacional, mini-mapa, cliente IXC, saúde (gauges).
 *
 * NÃO altera o backend. Os comandos disparam APENAS os tipos que
 * o backend aceita (ping, reboot, relay_on, relay_off,
 * relay_reboot, ota_update, config_*). Botões cujo comando não
 * existe no backend ficam DESABILITADOS com aviso — ver
 * UNSUPPORTED no grid de comandos.
 *
 * Mantém a assinatura { deviceId, refreshKey, onBack, onError }.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ackAlert,
  configSchedule,
  enqueueDeviceCommand,
  enqueueRelayReboot,
  enqueueSimpleCommand,
  getDeviceCenter,
  getDeviceConfigSnapshot,
  getFirmwareRelease,
  getFleetMapDevices,
  healthExplain,
  listCommands,
  listDeviceOtaEvents,
  listFirmwareReleases,
  listScheduleEvents,
  triggerOta,
  triggerDeviceFsOta,
  resetDeviceBootstrap,
} from "../lib/api.js";
import { fsCapabilityState, fsOtaErrorMessage } from "../lib/fsOta.js";
import {
  evaluateOtaCompat, classifyRelease, releaseTypeBadge, RESET_CONFIRM_PHRASE,
} from "../lib/otaCompat.js";
import { useLeaflet } from "../components/fleet/useLeaflet.js";
import ScheduleV2Card from "../components/fleet/ScheduleV2Card.jsx";
import ScheduleEditor from "../components/schedule/ScheduleEditor.jsx";
import DeviceInventoryCard from "../components/DeviceInventoryCard.jsx";
import DeviceEventTimeline from "../components/DeviceEventTimeline.jsx";
import EnergyHistoryPanel from "../components/EnergyHistoryPanel.jsx";
import StabilityHistoryPanel from "../components/StabilityHistoryPanel.jsx";
import ReplaceDeviceModal from "../components/ReplaceDeviceModal.jsx";
import { DEVICE_NAME_KIND_LABEL, deviceDisplayName, formatBytesKB } from "../lib/format.js";
import {
  extractPowerMonitor, fmtVolt, fmtAmp, fmtWatt, powerStatusLabel,
} from "../lib/powerMonitor.js";
import Can from "../components/Can.jsx";
import DeviceEditModal from "../components/DeviceEditModal.jsx";
import WatchdogConfigModal from "../components/WatchdogConfigModal.jsx";
import WifiConfigModal from "../components/WifiConfigModal.jsx";
import NetworkConfigModal from "../components/NetworkConfigModal.jsx";
import BindingAuthCard from "../components/BindingAuthCard.jsx";
import DeviceProtectionCard from "../components/DeviceProtectionCard.jsx";
import ConnProbeCard from "../components/ConnProbeCard.jsx";
import ConnHistoryPanel from "../components/ConnHistoryPanel.jsx";
import ClaimCodeManager from "../components/ClaimCodeManager.jsx";
import StagedOtaPlanPanel from "../components/StagedOtaPlanPanel.jsx";
import { CAP, isPlatformAdmin, hasRole } from "../lib/rbac.js";
import { getProtectionState } from "../lib/protection.js";
import { useCriticalConfirm } from "../components/ConfirmCriticalAction.jsx";
import NavIcon from "../nav/NavIcon.jsx";

// F1 (recovery HMAC) — ação "Re-provisionar HMAC" via bootstrap-reset.
// Mostra SÓ p/ platform admin (endpoint require_admin_token), device ONLINE e
// HMAC quebrado no DEVICE (heartbeat: auth_secret_set=false OU
// auth_diag.secure_channel=false — NÃO o data.device.secure_channel, que reflete
// só "backend tem secret"). Caminho oficial — substitui o rotate+/savecloud manual.
function HmacRecoveryAction({ data, online, notify, onDone }) {
  const [busy, setBusy] = useState(false);
  const confirmCritical = useCriticalConfirm(); // F2c — confirmação forte S3.10
  const d = data?.device || {};
  const hb = data?.heartbeat || {};
  const secretSet = hb?.cmd_diag?.auth_secret_set === true;
  const secure = hb?.auth_diag?.secure_channel === true;
  const hmacBroken = !secretSet || !secure;
  // F2a — só authorized + NÃO archived. Strict-equality = fail-safe: campo ausente
  // (DTO antigo, antes do rebuild) → undefined → oculta o botão por segurança.
  const authorized = d.authorization_status === "authorized";
  const notArchived = d.archived_at === null;
  if (!isPlatformAdmin() || !online || !hmacBroken || !d.device_id
      || !authorized || !notArchived) return null;

  async function onClick() {
    // F2c — confirmação forte S3.10 (senha da sessão ou X-Admin-Token).
    // O texto de risco vira o `impact` do modal; secret==null = cancelado.
    const secret = await confirmCritical({
      actionLabel: "Re-provisionar HMAC (bootstrap-reset)",
      target: { kind: "single", deviceId: d.device_id },
      impact:
        "Operação destrutiva do canal: LIMPA o secret ativo do backend (e o pending). " +
        "Use APENAS para RECOVERY (device authorized, NÃO archived, ONLINE e SEM HMAC). " +
        "NÃO use num device com HMAC funcionando — quebraria o canal temporariamente. " +
        "No próximo bootstrap o backend entrega um secret NOVO e o firmware o salva no NVS. " +
        "Depois aguarde 1–2 heartbeats e valide: auth_secret_set=true, auth_signed subindo, " +
        "auth_sign_fail=0, reason=ok, secure_channel=true.",
    });
    if (secret == null) return;
    setBusy(true);
    try {
      await resetDeviceBootstrap(d.device_id, { confirm: secret });
      notify?.("ok",
        "Bootstrap resetado. O device fará bootstrap limpo no próximo heartbeat e receberá um " +
        "secret novo (firmware salva no NVS). Aguarde ~1–2 min e valide o HMAC.");
      onDone?.();
    } catch (e) {
      notify?.("err",
        "Falha no re-provisionamento: " +
        (e?.detail?.message || e?.message || ("HTTP " + (e?.status || "?"))));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-noc-warn/40 bg-noc-warn/10
                    px-3 py-2 flex items-start gap-3">
      <div className="flex-1 text-xs text-noc-warn">
        <div className="font-semibold">🔑 Sem canal HMAC seguro — recovery disponível</div>
        <div className="opacity-80 mt-0.5">
          "Re-provisionar HMAC" limpa o secret no backend; o device (online, sem secret)
          recebe um novo no próximo bootstrap e o firmware salva no NVS. Caminho oficial
          de recovery — substitui o rotate + /savecloud manual.
        </div>
      </div>
      <button type="button" onClick={onClick} disabled={busy}
        className="shrink-0 text-xs rounded border border-noc-warn/60 text-noc-warn
                   px-3 py-1.5 hover:bg-noc-warn/20 disabled:opacity-50">
        {busy ? "Resetando…" : "Re-provisionar HMAC"}
      </button>
    </div>
  );
}
import {
  daysToWeekdayMask,
  buildConfigSchedulePayload,
  validateSchedule,
  validateScheduleForCapability,
  getSchedulerCapability,
  SCHEDULE_PULSE_DEFAULT_S,
  resolveActiveSchedule,
  compareScheduleSync,
  fmtScheduleEpoch,
} from "../lib/scheduleCore.js";
import { t } from "../i18n/index.js";

const TABS = [
  { id: "cockpit", label: "Visão geral" },
  { id: "telemetria", label: "Telemetria" },
  { id: "comandos", label: "Comandos" },
  { id: "ota", label: "Atualizações" },
  { id: "localizacao", label: "Mapa" },
  { id: "automacao", label: "Automação" },
  { id: "timeline", label: "Histórico" },
  // S7.7 Fase 1 — nova aba de validação da timeline consolidada (a aba
  // "Timeline" acima permanece intacta até a substituição definitiva).
  { id: "eventos", label: "Eventos" },
  { id: "cliente", label: "Cliente e saúde" },
];

export default function DeviceDetail({
  deviceId, initialTab, refreshKey, onBack, onError,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // P0-4 — deep-link de aba vindo do painel direito do Fleet Explorer.
  // Default preservado em "cockpit" se nenhum initialTab for passado.
  const [tab, setTab] = useState(initialTab || "cockpit");
  const [toast, setToast] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Sprint 6.5E — geo do device, do endpoint de fleet-map.
  const [geo, setGeo] = useState(null);
  // DEVICE-DETAIL-MANAGE-1B — modal de edição de metadados (grupo/tags/localização).
  const [editOpen, setEditOpen] = useState(false);
  // DEVICE-DETAIL-MANAGE-1D — modal de configuração do Watchdog.
  const [wdOpen, setWdOpen] = useState(false);
  // DEVICE-DETAIL-MANAGE-1E — modal de reconfiguração de Wi-Fi.
  const [wifiOpen, setWifiOpen] = useState(false);
  // DEVICE-DETAIL-MANAGE-1E2 — modal de configuração de IP/rede.
  const [netOpen, setNetOpen] = useState(false);
  // SAAS-CLAIM-UI — modal de códigos de vínculo (super_admin/field).
  const [claimOpen, setClaimOpen] = useState(false);

  // Localização — busca no fleet-map e filtra por este device.
  // Best-effort: se falhar, geo fica null (estado "sem coords").
  useEffect(() => {
    let alive = true;
    getFleetMapDevices()
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res)
          ? res
          : (res?.devices || res?.items || []);
        const found = list.find(
          (d) => d.device_id === deviceId
        );
        setGeo(found || null);
      })
      .catch(() => { if (alive) setGeo(null); });
    return () => { alive = false; };
  }, [deviceId, reloadKey]);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setErr(null);
    try {
      const d = await getDeviceCenter(deviceId, { signal });
      setData(d);
    } catch (e) {
      if (e?.name !== "AbortError") {
        setErr(e?.message || "Falha ao carregar o Device Center.");
        onError && onError(e);
      }
    } finally {
      setLoading(false);
    }
  }, [deviceId, onError]);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load, refreshKey, reloadKey]);

  // Telemetria live — repolling leve a cada 15s enquanto a tela
  // está aberta (o /center não é WS; 15s é suave para o backend).
  useEffect(() => {
    const id = setInterval(() => {
      getDeviceCenter(deviceId).then(setData).catch(() => {});
    }, 15000);
    return () => clearInterval(id);
  }, [deviceId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  function notify(kind, msg) { setToast({ kind, msg }); }
  function reload() { setReloadKey((k) => k + 1); }

  const d = data?.device || {};
  const online = isOnline(d.last_seen);

  return (
    <div className="device-detail-page space-y-5">
      <HmacRecoveryAction data={data} online={online} notify={notify} onDone={reload} />
      {/* ===== Cabeçalho ===== */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-xs font-semibold
                     text-noc-muted hover:text-noc-accent transition-colors"
        >
          <span aria-hidden="true">←</span>
          Voltar para dispositivos
        </button>
        <div className="flex-1" />
        <div className="flex flex-wrap items-center gap-2">
          <Can capability={CAP.FLEET_WRITE}>
            <button
              onClick={() => setEditOpen(true)}
              className="btn-ghost h-10 gap-2"
            >
              <NavIcon name="gear" size={15} />
              Editar informações
            </button>
          </Can>
          {hasRole("super_admin", "field") && (
            <button
              onClick={() => setClaimOpen(true)}
              disabled={getProtectionState(data).protected}
              title={getProtectionState(data).protected
                ? "Dispositivo protegido — geração de código bloqueada"
                : "Gerar código de vínculo para o cliente"}
              className="btn-ghost h-10 gap-2
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <NavIcon name="qr" size={15} />
              Gerar vínculo
            </button>
          )}
          <button onClick={reload} className="btn-primary h-10 gap-2">
            <NavIcon name="refresh" size={15} />
            Atualizar
          </button>
        </div>
      </div>

      {err && (
        <div className="text-sm text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded-lg p-3">
          {err}
        </div>
      )}
      {loading && !data && (
        <div className="text-sm font-mono text-[12px] text-noc-faint">
          Carregando o cockpit…
        </div>
      )}

      {data && (
        <>
          {/* FASE 1 / Etapa 4B — hero cockpit Command Deck: identidade +
              chips operacionais + ações rápidas (handlers reais). */}
          <CockpitHeader
            deviceId={deviceId} data={data} online={online}
            notify={notify} onAfterAction={reload} onOpenTab={setTab}
          />

          {/* S7.6-A — Device Inventory Card: card de identidade fixo
              acima das abas, sempre visível independente da aba ativa.
              Lê de data.device + data.inventory + data.binding +
              data.heartbeat (todos já vêm do /center). */}
          <DeviceInventoryCard data={data} />

          {/* DEVICE-DETAIL-MANAGE-1B — modal de edição de grupo/tags/localização.
              Usa endpoints já existentes/guardados; confirma forte se protegido. */}
          {editOpen && (
            <DeviceEditModal
              deviceId={deviceId}
              device={d}
              geo={geo}
              onClose={() => setEditOpen(false)}
              onSaved={() => {
                setEditOpen(false);
                reload();
                notify("ok", "Informações atualizadas.");
              }}
            />
          )}

          {/* DEVICE-DETAIL-MANAGE-1D — modal de configuração do Watchdog (endpoint
              existente config/watchdog, confirmação forte sempre). */}
          {wdOpen && (
            <WatchdogConfigModal
              deviceId={deviceId}
              device={d}
              watchdog={data.watchdog_runtime}
              onClose={() => setWdOpen(false)}
              onSaved={() => {
                setWdOpen(false);
                reload();
                notify("ok", "Watchdog: configuração enviada ao device.");
              }}
            />
          )}

          {/* DEVICE-DETAIL-MANAGE-1E — modal de reconfiguração de Wi-Fi (endpoint
              existente config/wifi, confirmação forte sempre). */}
          {wifiOpen && (
            <WifiConfigModal
              deviceId={deviceId}
              device={d}
              onClose={() => setWifiOpen(false)}
              onSaved={() => {
                setWifiOpen(false);
                reload();
                notify("ok", "Wi-Fi: configuração enviada ao device (aplica no próximo heartbeat).");
              }}
            />
          )}

          {/* DEVICE-DETAIL-MANAGE-1E2 — modal de IP/rede (endpoint config/network). */}
          {netOpen && (
            <NetworkConfigModal
              deviceId={deviceId}
              device={d}
              onClose={() => setNetOpen(false)}
              onSaved={() => {
                setNetOpen(false);
                reload();
                notify("ok", "Rede: configuração enviada ao device (aplica no próximo heartbeat).");
              }}
            />
          )}

          {/* SAAS-CLAIM-UI — modal de códigos de vínculo (super_admin/field). */}
          {claimOpen && (
            <ClaimCodeManager
              deviceId={deviceId}
              data={data}
              notify={notify}
              onClose={() => setClaimOpen(false)}
            />
          )}

          {/* Navegação contextual */}
          <div className="sticky top-[68px] z-20 glass-card p-1.5
                          flex gap-1 overflow-x-auto
                          shadow-[0_12px_30px_rgba(2,8,18,0.18)]">
            {TABS.map((tb) => (
              <button
                key={tb.id}
                onClick={() => setTab(tb.id)}
                className={
                  "px-3.5 py-2 text-xs font-semibold whitespace-nowrap "
                  + "rounded-lg transition-all "
                  + (tab === tb.id
                      ? "bg-noc-accent/12 text-noc-accent "
                        + "shadow-[inset_0_0_0_1px_rgba(84,224,212,0.2)]"
                      : "text-noc-muted hover:text-noc-text "
                        + "hover:bg-noc-panel2")
                }
              >
                {tb.label}
              </button>
            ))}
          </div>

          {tab === "cockpit" && (
            <CockpitTab
              deviceId={deviceId} data={data} geo={geo}
              notify={notify} onOpenTab={setTab}
            />
          )}
          {tab === "telemetria" && <TelemetryTab data={data} />}
          {tab === "comandos" && (
            /* Etapa 4B — o grid de disparo migrou do cockpit para cá:
               disparo + histórico na mesma aba (cockpit segue o mockup). */
            <div className="grid lg:grid-cols-3 gap-4 items-start">
              <CommandGrid
                deviceId={deviceId} notify={notify}
                otaProtected={getProtectionState(data).protected}
                onAfterAction={reload}
                onConfigWatchdog={() => setWdOpen(true)}
                onConfigWifi={() => setWifiOpen(true)}
                onConfigNetwork={() => setNetOpen(true)}
              />
              <div className="lg:col-span-2">
                <CommandsTab
                  deviceId={deviceId} notify={notify}
                  otaProtected={getProtectionState(data).protected}
                  refreshKey={reloadKey}
                />
              </div>
            </div>
          )}
          {tab === "ota" && (
            <OtaTab
              deviceId={deviceId} data={data}
              notify={notify} onAfterAction={reload}
            />
          )}
          {tab === "localizacao" && (
            <LocationTab
              deviceId={deviceId} data={data} geo={geo}
              notify={notify}
            />
          )}
          {tab === "automacao" && (
            <AutomationTab
              deviceId={deviceId} data={data} notify={notify}
            />
          )}
          {tab === "timeline" && <TimelineTab data={data} />}
          {tab === "eventos" && <DeviceEventTimeline deviceId={deviceId} />}
          {tab === "cliente" && (
            <ClientHealthTab
              data={data} deviceId={deviceId}
              notify={notify} onAfterAction={reload}
            />
          )}
        </>
      )}

      {toast && (
        <div className={
          "fixed bottom-4 right-4 z-50 px-4 py-2.5 rounded-lg "
          + "text-sm shadow-lg border max-w-sm "
          + (toast.kind === "error"
              ? "bg-noc-err/10 border-noc-err/30 text-noc-err"
              : "bg-noc-ok/10 border-noc-ok/30 "
                + "text-noc-ok")
        }>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ════════ FASE 1 / Etapa 4B — Hero cockpit (mockup Command Deck) ════════
 *
 * Identidade (LED + device_id + chips de status), linha do cliente IXC,
 * chips operacionais FW/IP/RSSI/HEAP/UPTIME/RELAY e AÇÕES RÁPIDAS.
 * Os botões reusam os MESMOS comandos/confirmações do CommandGrid
 * (S3.10A) — nenhum bypass de confirmação crítica.
 */
function CockpitHeader({
  deviceId, data, online, notify, onAfterAction, onOpenTab,
}) {
  const d = data.device || {};
  const b = data.binding || {};
  const inv = data.inventory || {};
  const h = data.health || {};
  const hb = data.heartbeat || {};
  const openAlerts = (data.alerts || []).filter(
    (a) => a.status === "open"
  ).length;
  // RELAY — estado real do heartbeat (hb.relay="on"/"off",
  // hb.relay_state=bool). Firmware antigo sem o campo → "—".
  const relay = hb.relay_state === true || hb.relay === "on"
    ? "on"
    : (hb.relay_state === false || hb.relay === "off" ? "off" : null);

  // NOC-UX-DEVICES-IDENTITY-1 — nome amigável em destaque (login PPPoE do cliente
  // IXC ou nome manual do ponto) no lugar do device_id; o device_id vira linha
  // secundária (chave de hardware, nunca some).
  const ident = deviceDisplayName({
    pppoe_login: b.pppoe_login,
    operational_identifier: d.operational_identifier,
    customer_code: d.customer_code,
    device_id: deviceId,
  });
  const identIsId = ident.kind === "id";
  const identTag = (ident.kind === "pppoe" || ident.kind === "point")
    ? DEVICE_NAME_KIND_LABEL[ident.kind] : null;

  const clientLine = [
    // o PPPoE agora é o título quando presente — não repete aqui.
    (inv.qr_serial || b.qr_serial) ? `QR ${inv.qr_serial || b.qr_serial}` : null,
    `último HB ${fmtTime(d.last_seen)}`,
  ].filter(Boolean).join(" · ");

  return (
    <div className="glass-card-pad !p-5 sm:!p-6 overflow-hidden relative">
      <div className="flex items-start gap-5 flex-wrap">
        {/* Identidade */}
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={online ? "led-ok" : "led-err"} />
            <h1 className={
              "m-0 text-2xl sm:text-3xl font-bold tracking-tight text-noc-text break-all "
              + (identIsId ? "font-mono" : "")
            }>
              {ident.label}
            </h1>
            {identTag && (
              <span className="font-mono text-[9px] font-bold uppercase tracking-wider
                               text-noc-faint border border-noc-border/70 rounded
                               px-1.5 py-0.5">
                {identTag}
              </span>
            )}
            <span className={
              "font-mono text-[10px] tracking-[0.14em] rounded-md "
              + "border px-2 py-0.5 "
              + (online
                  ? "text-noc-ok border-noc-ok/40 bg-noc-ok/10"
                  : "text-noc-err border-noc-err/40 bg-noc-err/10")
            }>
              {online ? "ONLINE" : "OFFLINE"}
            </span>
            {b.contract_id && (
              <span className="font-mono text-[9px] font-bold
                               text-noc-accent border
                               border-noc-accent/40 rounded
                               px-1.5 py-0.5">
                IXC #{b.contract_id}
              </span>
            )}
            <Badge tone={healthTone(h.health_status)}>
              {(h.health_status || "—").toUpperCase()}
            </Badge>
            {/* PROTECTED-DEVICES-MANAGE-1E — proteção EFETIVA (ENV OR DB). Informativo;
                a trava real é no backend (guard em enqueue_command). */}
            {getProtectionState(data).protected && (
              <span
                className="font-mono text-[9px] font-bold inline-flex items-center
                           gap-1 text-violet-300 border border-violet-400/40
                           bg-violet-400/10 rounded px-1.5 py-0.5"
                title="Protegido (ENV ou device_protections). OTA/relé/reboot/config bloqueados pelo backend."
              >
                🔒 PROTEGIDO
              </span>
            )}
            {/* PROTECTED-DEVICES-MANAGE-1E — unidade-ouro/referência (protection_source
                = 'reference', data-driven). Pede confirmação em ping e atrito no reenvio. */}
            {getProtectionState(data).source === "reference" && (
              <span
                className="font-mono text-[9px] font-bold inline-flex items-center
                           gap-1 text-amber-300 border border-amber-400/40
                           bg-amber-400/10 rounded px-1.5 py-0.5"
                title="Unidade referência (Gold) — não receber comandos acidentais. Ping pede confirmação; reenvio de comando mutante é bloqueado na UI."
              >
                ⭐ GOLD / REFERÊNCIA
              </span>
            )}
          </div>

          {/* NOC-UX-DEVICES-IDENTITY-1 — device_id (chave de hardware) como linha
              secundária quando o título já é o nome amigável. Nunca some. */}
          {!identIsId && (
            <div className="mt-1 font-mono text-xs text-noc-muted break-all">
              {deviceId}
            </div>
          )}

          {/* Linha cliente */}
          <div className="flex items-center gap-2 mt-2 text-xs
                          flex-wrap">
            <span className="text-noc-text2 font-semibold">
              {b.customer_id || d.customer_code || "Sem vínculo IXC"}
            </span>
            <span className="text-noc-faint">{clientLine}</span>
          </div>

          {/* Chips operacionais */}
          <div className="flex gap-2.5 mt-4 flex-wrap">
            <OpChip k="FW" v={d.fw_version || "—"} />
            <OpChip k="IP" v={d.ip_last || "—"} />
            <OpChip k="RSSI"
                    v={d.rssi != null ? `${d.rssi} dBm` : "—"}
                    tone={d.rssi != null ? rssiTone(d.rssi) : ""} />
            <OpChip k="HEAP"
                    v={hb.heap_free != null
                        ? formatBytesKB(hb.heap_free) : "—"} />
            <OpChip k="UPTIME"
                    v={fmtUptime(hb.uptime_s ?? h.uptime_s)} />
            <OpChip k="RELAY"
                    v={relay == null
                        ? "—" : relay === "on" ? "ARMADO" : "DESARMADO"}
                    tone={relay === "on" ? "text-noc-ok"
                          : relay === "off" ? "text-noc-warn" : ""}
                    box={relay === "on"
                          ? "border-noc-ok/30 bg-noc-ok/5"
                          : relay === "off"
                            ? "border-noc-warn/30 bg-noc-warn/5" : ""} />
            {openAlerts > 0 && (
              <OpChip k="ALERTAS" v={String(openAlerts)}
                      tone="text-noc-err"
                      box="border-noc-err/30 bg-noc-err/5" />
            )}
          </div>
        </div>

        {/* Ações rápidas */}
        <QuickActions
          deviceId={deviceId} notify={notify}
          otaProtected={getProtectionState(data).protected}
          onAfterAction={onAfterAction} onOpenTab={onOpenTab}
        />
      </div>
    </div>
  );
}

/** Chip operacional do hero (FW/IP/RSSI/HEAP/UPTIME/RELAY). */
function OpChip({ k, v, tone, box }) {
  return (
    <span className={
      "inline-flex items-center gap-1.5 font-mono text-[11px] "
      + "rounded-lg border px-2.5 py-1.5 "
      + (box || "border-noc-border bg-noc-bg/60") + " "
      + (tone || "text-noc-text2")
    }>
      <span className="text-noc-faint">{k}</span>
      {v}
    </span>
  );
}

/**
 * AÇÕES RÁPIDAS do hero — reusa os MESMOS comandos e o MESMO fluxo de
 * confirmação do CommandGrid (S3.10A): Reboot relay e Reiniciar ESP
 * pedem senha/ADMIN_TOKEN; Ping é livre; "Ver no mapa" só troca de aba.
 * Gate de capability idêntico (CAP.DEVICES_COMMAND).
 */
// PROTECTED-DEVICES-MANAGE-1E — devices SENSÍVEIS = proteção EFETIVA (ENV OR DB),
// vinda do backend (getProtectionState → prop `otaProtected` agora carrega o efetivo).
// Antes havia a lista hardcoded REFERENCE_DEVICE_IDS p/ o gold; aposentada — o sinal
// é data-driven (protection_source). A trava real continua no enqueue_command guard.
function deviceSensitivity(_deviceId, effectiveProtected) {
  if (effectiveProtected) {
    return { sensitive: true, kind: "protected", label: "dispositivo protegido" };
  }
  return { sensitive: false, kind: "normal", label: "" };
}
// Mensagem de confirmação do Ping em device sensível (null = não confirma).
function pingConfirmMsg(deviceId, otaProtected) {
  const s = deviceSensitivity(deviceId, otaProtected);
  if (!s.sensitive) return null;
  return `Este dispositivo é ${s.label.toUpperCase()}. O ping é benigno mas cria `
    + "1 comando no histórico. Confirmar ping?";
}

function QuickActions({ deviceId, notify, onAfterAction, onOpenTab, otaProtected }) {
  const confirmCritical = useCriticalConfirm(); // S3.10A
  const [busy, setBusy] = useState(null);
  const pingMsg = pingConfirmMsg(deviceId, otaProtected);  // null em device normal

  async function fire(label, fn, { critical } = {}) {
    let confirm;
    if (critical) {
      const secret = await confirmCritical({
        actionLabel: label,
        target: { kind: "single", deviceId },
        impact: "Esta ação afeta o equipamento em campo.",
      });
      if (secret == null) return;
      confirm = secret;
    }
    setBusy(label);
    try {
      await fn(confirm);
      notify("ok", `${label} — comando enviado.`);
      onAfterAction && onAfterAction();
    } catch (e) {
      notify("error", `${label} falhou: ${e?.message || "erro"}`);
    } finally {
      setBusy(null);
    }
  }

  const base = "h-9 px-3 rounded-lg text-xs font-semibold border "
    + "transition-colors disabled:opacity-40 ";

  return (
    <div className="flex flex-col gap-2 min-w-[220px] rounded-xl border border-noc-border bg-noc-bg/35 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em]
                      text-noc-faint">
        Ações rápidas
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Can
          capability={CAP.DEVICES_COMMAND}
          fallback={
            <div className="col-span-2 text-[11px] font-mono
                            text-noc-faint">
              Somente leitura — seu perfil não pode enviar comandos.
            </div>
          }
        >
          <button
            disabled={busy !== null}
            onClick={() => fire(
              "Reiniciar Relé",
              (confirm) => enqueueRelayReboot(deviceId, 5, { confirm }),
              { critical: true },
            )}
            className={base + "border-noc-err/35 text-noc-err "
              + "bg-noc-err/5 hover:bg-noc-err/15"}
          >
            {busy === "Reiniciar Relé" ? "…" : "Reiniciar Relé"}
          </button>
          <button
            disabled={busy !== null}
            onClick={() => fire(
              "Reiniciar ESP",
              (confirm) =>
                enqueueDeviceCommand(deviceId, "reboot", { confirm }),
              { critical: true },
            )}
            className={base + "border-noc-border text-noc-text2 "
              + "hover:border-noc-accent/40 hover:text-noc-text"}
          >
            {busy === "Reiniciar ESP" ? "…" : "Reiniciar ESP"}
          </button>
          <button
            disabled={busy !== null}
            onClick={() => {
              if (pingMsg && !window.confirm(pingMsg)) return;
              fire("Ping", () => enqueueDeviceCommand(deviceId, "ping"));
            }}
            className={base + "border-noc-border text-noc-text2 "
              + "hover:border-noc-accent/40 hover:text-noc-text"}
          >
            {busy === "Ping" ? "…" : "Ping"}
          </button>
        </Can>
        <button
          onClick={() => onOpenTab && onOpenTab("localizacao")}
          className={base + "border-noc-accent/35 text-noc-accent "
            + "bg-noc-accent/5 hover:bg-noc-accent/15"}
        >
          Ver no mapa
        </button>
      </div>
    </div>
  );
}

/* ════════ FASE 2 — Grid de comandos operacionais ════════ */
/**
 * Mapa de comandos. `type` é o que o backend aceita; comandos
 * marcados `unsupported` NÃO existem no backend — o botão fica
 * desabilitado com aviso (não inventamos endpoints).
 */
function CommandGrid({ deviceId, notify, onAfterAction, otaProtected, onConfigWatchdog, onConfigWifi, onConfigNetwork }) {
  const [busy, setBusy] = useState(null);
  const confirmCritical = useCriticalConfirm(); // S3.10A

  async function fire(label, fn, { critical, danger, confirmMsg } = {}) {
    let confirm;
    if (critical) {
      // S3.10A — ação crítica: confirmação com senha (usuário) ou ADMIN_TOKEN (bancada).
      const secret = await confirmCritical({
        actionLabel: label,
        target: { kind: "single", deviceId },
        impact: "Esta ação afeta o equipamento em campo.",
      });
      if (secret == null) return;
      confirm = secret;
    } else if (confirmMsg && !window.confirm(confirmMsg)) {
      // confirmação leve (ex.: ping em device protegido) — sem credencial.
      return;
    } else if (danger && !window.confirm(
      `Confirmar: ${label}?\n\nEsta ação afeta o equipamento em campo.`
    )) {
      return;
    }
    setBusy(label);
    try {
      await fn(confirm);
      notify("ok", `${label} — comando enviado.`);
      onAfterAction && onAfterAction();
    } catch (e) {
      notify("error", `${label} falhou: ${e?.message || "erro"}`);
    } finally {
      setBusy(null);
    }
  }

  // Botão de comando real.
  const Cmd = ({ label, run, variant, danger, critical, confirmMsg }) => (
    <button
      onClick={() => fire(label, run, { danger, critical, confirmMsg })}
      disabled={busy !== null}
      className={
        "h-10 px-2 rounded-lg text-xs font-semibold border "
        + "transition-all hover:-translate-y-0.5 "
        + "disabled:opacity-40 disabled:translate-y-0 " + variant
      }
    >
      {busy === label ? "…" : label}
    </button>
  );

  // Botão de comando SEM backend — desabilitado, com tooltip.
  const Unsupported = ({ label }) => (
    <button
      disabled
      title="Comando ainda não disponível no backend"
      className="h-10 px-2 rounded-lg text-xs font-semibold border
                 border-noc-border/50 text-noc-muted/50
                 bg-noc-panel2/30 cursor-not-allowed"
    >
      {label} ⚠
    </button>
  );

  const safe = "border-noc-border text-noc-text "
    + "hover:border-noc-accent/50";
  const warn = "border-noc-warn/30 text-noc-warn "
    + "bg-noc-warn/10";
  const danger = "border-noc-err/30 text-noc-err bg-noc-err/10";

  return (
    <div className="glass-card-pad space-y-3">
      <div className="text-xs font-semibold uppercase
                      tracking-wider text-noc-muted">
        Comandos operacionais
      </div>

      <Can
        capability={CAP.DEVICES_COMMAND}
        fallback={
          <div className="text-xs font-mono text-[12px] text-noc-faint">
            Somente leitura — seu perfil não pode enviar comandos.
          </div>
        }
      >
      <CmdGroup title="Energia">
        {/* NOC-UX — ligar o relé em campo é tão impactante quanto desligar:
            confirmação forte como relay_off. command_type "relay_on" inalterado. */}
        <Cmd label="Relé LIGADO" variant={warn} critical
             run={(confirm) => enqueueDeviceCommand(deviceId, "relay_on", { confirm })} />
        <Cmd label="Relé DESLIGADO" variant={warn} critical
             run={(confirm) => enqueueDeviceCommand(deviceId, "relay_off", { confirm })} />
        <Cmd label="Reiniciar Relé" variant={danger} critical
             run={(confirm) => enqueueRelayReboot(deviceId, 5, { confirm })} />
      </CmdGroup>

      <CmdGroup title="Sistema">
        <Cmd label="Reiniciar ESP" variant={danger} critical
             run={(confirm) => enqueueDeviceCommand(deviceId, "reboot", { confirm })} />
        <Cmd label="Ping" variant={safe}
             confirmMsg={pingConfirmMsg(deviceId, otaProtected)}
             run={() => enqueueDeviceCommand(deviceId, "ping")} />
        <Unsupported label="Heartbeat" />
        <Unsupported label="Sincronização Segura" />
      </CmdGroup>

      <CmdGroup title="Rede">
        {/* DEVICE-DETAIL-MANAGE-1E — Reconfigurar WiFi real, gated por devices:config */}
        <Can capability={CAP.DEVICES_CONFIG}
             fallback={<Unsupported label="Reconfigurar WiFi (sem permissão)" />}>
          <button
            onClick={onConfigWifi}
            className={"h-10 px-2 rounded-lg text-xs font-semibold border "
              + "transition-all hover:-translate-y-0.5 " + safe}
          >
            📶 Reconfigurar WiFi
          </button>
        </Can>
        {/* DEVICE-DETAIL-MANAGE-1E2 — Configurar IP/Rede (DHCP/estático), devices:config */}
        <Can capability={CAP.DEVICES_CONFIG}
             fallback={<Unsupported label="Configurar IP/Rede (sem permissão)" />}>
          <button
            onClick={onConfigNetwork}
            className={"h-10 px-2 rounded-lg text-xs font-semibold border "
              + "transition-all hover:-translate-y-0.5 " + safe}
          >
            🌐 Configurar IP/Rede
          </button>
        </Can>
        <Unsupported label="Forçar reconexão" />
        {/* DEVICE-DETAIL-MANAGE-1D — Watchdog real (config), gated por watchdog:write */}
        <Can capability={CAP.WATCHDOG_WRITE}
             fallback={<Unsupported label="Watchdog (sem permissão)" />}>
          <button
            onClick={onConfigWatchdog}
            className={"h-10 px-2 rounded-lg text-xs font-semibold border "
              + "transition-all hover:-translate-y-0.5 " + safe}
          >
            ⚙ Configurar Watchdog
          </button>
        </Can>
      </CmdGroup>
      </Can>

      <CmdGroup title="Ações perigosas">
        <Unsupported label="Reset de Fábrica" />
        <Unsupported label="Remover vinculação" />
      </CmdGroup>

      <div className="text-[10px] text-noc-muted/70 leading-relaxed">
        Botões com ⚠ ainda não têm comando correspondente no
        backend e ficam desabilitados — serão habilitados quando o
        backend expuser esses tipos. Comandos de energia e reinício
        pedem confirmação.
      </div>
    </div>
  );
}

function CmdGroup({ title, children }) {
  return (
    <div>
      <div className="font-mono text-[9.5px] uppercase tracking-[0.14em]
                      text-noc-muted/80 mb-1.5">
        {title}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {children}
      </div>
    </div>
  );
}

/* ════════ Cockpit — grid 3 colunas (Etapa 4B, mockup Command Deck) ════════
 *
 * Col 1: telemetria live + resumo do agendamento; Col 2: timeline
 * operacional (preview); Col 3: mini-mapa + cliente IXC. O grid de
 * comandos migrou para a aba Comandos; o painel OTA do cockpit foi
 * removido (duplicava a aba OTA e tinha um confirmCritical sem hook —
 * ReferenceError latente ao clicar).
 */
function CockpitTab({ deviceId, data, geo, notify, onOpenTab }) {
  // ENERGY-HISTORY-RESPONSIVE — gráfico Energia 24h COMPLETO vive aqui (página
  // responsiva, não a tabela). Só p/ device INA226 (pm != null).
  const pm = extractPowerMonitor(data.heartbeat);
  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-3 gap-5 items-start">
        {/* Col 1 — telemetria live + agendamento */}
        <div className="space-y-4">
          <CockpitTelemetryLive data={data} />
          <CockpitScheduleCard
            deviceId={deviceId} data={data} onOpenTab={onOpenTab}
          />
        </div>
        {/* Col 2 — timeline operacional */}
        <CockpitTimeline data={data} onOpenTab={onOpenTab} />
        {/* Col 3 — mapa + cliente */}
        <div className="space-y-4">
          <MiniMap data={data} geo={geo} deviceId={deviceId}
                   notify={notify}
                   onOpenMap={() => onOpenTab && onOpenTab("localizacao")} />
          <ClientCard data={data} />
        </div>
      </div>
      {/* Energia 24h full-width abaixo do cockpit (só INA226). */}
      {pm && <EnergyHistoryPanel deviceId={deviceId} variant="full" />}
      {/* FW-STABILITY-SOAK-1 — estabilidade (heap/uptime/reboots) p/ TODOS os
          devices, abaixo do cockpit. Read-only (heartbeats_raw). */}
      <StabilityHistoryPanel deviceId={deviceId} hours={72} />
    </div>
  );
}

/* "// TELEMETRIA LIVE" — col 1 do cockpit (mockup): RSSI com barra,
   heap livre, uptime, último reboot e heartbeat. Mesmas fontes reais
   do /center (device + heartbeat); a lista completa segue na aba
   Telemetria (TelemetrySummary). */
function CockpitTelemetryLive({ data }) {
  const d = data.device || {};
  const h = data.health || {};
  const hb = data.heartbeat || {};
  // INA226-2B — energia (INA226) do heartbeat cru. null em device comum.
  const pm = extractPowerMonitor(hb);
  const online = isOnline(d.last_seen);
  const rssiBar = d.rssi == null ? "" :
    d.rssi >= -65 ? "bg-noc-ok" : d.rssi >= -75 ? "bg-noc-warn"
      : "bg-noc-err";
  const lastReboot = [
    hb.reboot_reason || d.last_boot_reason,
    hb.boot_count != null ? `boot #${hb.boot_count}` : null,
  ].filter(Boolean).join(" · ") || "—";

  return (
    <div className="glass-card-pad space-y-4">
      <h2 className="section-label">{"Telemetria live"}</h2>
      <div>
        <div className="flex justify-between items-baseline">
          <span className="font-mono text-[10px] tracking-[0.12em]
                           text-noc-faint">
            RSSI
          </span>
          <span className={
            "font-mono text-base font-semibold "
            + (d.rssi != null ? rssiTone(d.rssi) : "text-noc-muted")
          }>
            {d.rssi != null ? `${d.rssi} dBm` : "—"}
          </span>
        </div>
        {d.rssi != null && (
          <div className="h-1.5 rounded bg-noc-bg mt-1.5
                          overflow-hidden">
            <div className={"h-full rounded " + rssiBar}
                 style={{ width: rssiPct(d.rssi) + "%" }} />
          </div>
        )}
      </div>
      <div className="flex justify-between items-baseline">
        <span className="font-mono text-[10px] tracking-[0.12em]
                         text-noc-faint">
          HEAP LIVRE
        </span>
        <span className="font-mono text-base font-semibold
                         text-noc-accent">
          {hb.heap_free != null ? formatBytesKB(hb.heap_free) : "—"}
        </span>
      </div>
      <div className="flex justify-between items-baseline">
        <span className="font-mono text-[10px] tracking-[0.12em]
                         text-noc-faint">
          UPTIME
        </span>
        <span className="font-mono text-base font-semibold
                         text-noc-text">
          {fmtUptime(hb.uptime_s ?? h.uptime_s)}
        </span>
      </div>
      <div className="flex justify-between items-baseline gap-3">
        <span className="font-mono text-[10px] tracking-[0.12em]
                         text-noc-faint shrink-0">
          ÚLT. REBOOT
        </span>
        <span className="font-mono text-xs text-noc-text2 truncate">
          {lastReboot}
        </span>
      </div>
      <div className="flex justify-between items-center">
        <span className="font-mono text-[10px] tracking-[0.12em]
                         text-noc-faint">
          HEARTBEAT
        </span>
        <span className="flex items-center gap-2">
          <span className={
            (online ? "led-ok animate-pulse" : "led-err")
          } />
          <span className={
            "font-mono text-xs "
            + (online ? "text-noc-ok" : "text-noc-err")
          }>
            {fmtTime(d.last_seen)}
          </span>
        </span>
      </div>
      {/* INA226-2B — Energia (INA226). Só p/ device compatível (modelo
          esp32c3-ina226); device comum não renderiza nada. Com sensor ok:
          tensão/corrente/potência; sem sensor: só o status (âmbar). */}
      {pm && (
        <div className="space-y-2 pt-3 border-t border-noc-border/30">
          {pm.ok && (
            <>
              <div className="flex justify-between items-baseline">
                <span className="font-mono text-[10px] tracking-[0.12em] text-noc-faint">
                  ENERGIA · TENSÃO
                </span>
                <span className="font-mono text-base font-semibold text-noc-ok">
                  {fmtVolt(pm.busMv) || "—"}
                </span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="font-mono text-[10px] tracking-[0.12em] text-noc-faint">
                  CORRENTE
                </span>
                <span className="font-mono text-base font-semibold text-noc-ok">
                  {fmtAmp(pm.currentMa) || "—"}
                </span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="font-mono text-[10px] tracking-[0.12em] text-noc-faint">
                  POTÊNCIA
                </span>
                <span className="font-mono text-base font-semibold text-noc-accent">
                  {fmtWatt(pm.powerMw) || "—"}
                </span>
              </div>
            </>
          )}
          <div className="flex justify-between items-baseline gap-3">
            <span className="font-mono text-[10px] tracking-[0.12em] text-noc-faint shrink-0">
              {pm.ok ? "SENSOR" : "ENERGIA · SENSOR"}
            </span>
            <span className={
              "font-mono text-xs truncate "
              + (pm.bad ? "text-noc-warn" : "text-noc-text2")
            }>
              {powerStatusLabel(pm)}
              {pm.address != null ? ` · 0x${pm.address.toString(16).toUpperCase()}` : ""}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/* "// AGENDAMENTO" — resumo READ-ONLY da agenda REAL do device.
   Mesma fonte da aba Automação (config_snapshot + resolveActiveSchedule
   do scheduleCore — autoridade = device). Não edita nada: a edição
   continua na aba Automação via config_schedule (ADV-C1). */
function CockpitScheduleCard({ deviceId, data, onOpenTab }) {
  const [snap, setSnap] = useState(null);
  const [known, setKnown] = useState(false);

  useEffect(() => {
    let alive = true;
    getDeviceConfigSnapshot(deviceId)
      .then((r) => {
        if (!alive) return;
        setSnap(r?.last_config_snapshot || null);
        setKnown(true);
      })
      .catch(() => { if (alive) { setSnap(null); setKnown(false); } });
    return () => { alive = false; };
  }, [deviceId]);

  const resolved = resolveActiveSchedule(snap, data);
  const entries = known ? (resolved.active.entries || []) : [];
  const next = known
    ? nextOccurrenceFromEntries(entries, new Date()) : null;
  const actionLabel = (a) =>
    (SCHED_ACTIONS.find((s) => s.id === a) || {}).label
      || a || "Reiniciar Relé";

  return (
    <div className="glass-card-pad">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="section-label flex-1">{"Agendamento"}</h2>
        <button
          onClick={() => onOpenTab && onOpenTab("automacao")}
          className="font-mono text-[10px] tracking-[0.1em]
                     text-noc-accent hover:underline"
        >
          AUTOMAÇÃO →
        </button>
      </div>
      {!known ? (
        <div className="text-[11px] font-mono text-noc-faint py-1.5">
          Sem snapshot de configuração do dispositivo.
        </div>
      ) : entries.length === 0 ? (
        <div className="text-[11px] font-mono text-noc-faint py-1.5">
          Nenhum agendamento ativo no dispositivo.
        </div>
      ) : (
        <>
          {entries.slice(0, 4).map((e, i) => (
            <div key={i}
                 className="flex items-center gap-2.5 py-2 text-xs
                            border-b border-noc-divider/60
                            last:border-0">
              <span className={
                resolved.active.enabled !== false ? "led-ok" : "led-off"
              } />
              <span className="flex-1 text-noc-text2 truncate">
                {actionLabel(e.action)}
              </span>
              <span className="font-mono text-[11px] text-noc-muted
                               whitespace-nowrap">
                {fmtDaysShort(e.days)} {e.time}
              </span>
            </div>
          ))}
          {entries.length > 4 && (
            <div className="text-[10px] font-mono text-noc-faint pt-1">
              +{entries.length - 4} agendamentos…
            </div>
          )}
          <div className="flex justify-between pt-2 text-[10px]
                          font-mono text-noc-faint">
            <span>PRÓXIMA</span>
            <span className="text-noc-text2">
              {next ? fmtTime(next) : "—"}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/* "// TIMELINE OPERACIONAL" — col 2 do cockpit (mockup): preview dos
   últimos eventos de data.timeline (mesma fonte da aba Timeline) +
   atalho para a timeline completa. */
function CockpitTimeline({ data, onOpenTab }) {
  const events = (data.timeline || []).slice(0, 6);
  return (
    <div className="glass-card-pad">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="section-label flex-1">
          {"Timeline operacional"}
        </h2>
        <span className="font-mono text-[9.5px] text-noc-faint">
          RECENTES
        </span>
      </div>
      {events.length === 0 ? (
        <div className="text-[11px] font-mono text-noc-faint py-2">
          Sem eventos na timeline operacional.
        </div>
      ) : (
        <div>
          {events.map((e, i) => (
            <div key={i}
                 className="flex gap-3 py-2 border-b
                            border-noc-divider/60 last:border-0">
              <span className="font-mono text-[10.5px] text-noc-faint
                               w-[76px] shrink-0 pt-0.5">
                {fmtTime(e.created_at || e.at || e.timestamp)}
              </span>
              <span className={
                "mt-1.5 "
                + (e.result === "fail" || e.result === "error"
                    ? "led-err"
                    : e.result === "ok" || e.result === "success"
                      ? "led-ok" : "led-info")
              } />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-noc-text
                                truncate">
                  {e.action || e.event || e.type || "evento"}
                </div>
                <div className="font-mono text-[10.5px] text-noc-faint
                                truncate">
                  {[e.actor || e.user || "sistema", e.notes]
                    .filter(Boolean).join(" · ")}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={() => onOpenTab && onOpenTab("timeline")}
        className="w-full mt-2.5 font-mono text-[10px]
                   tracking-[0.12em] text-noc-accent border
                   border-dashed border-noc-accent/30 rounded-lg
                   py-2 hover:bg-noc-accent/5 transition-colors"
      >
        VER TIMELINE COMPLETA →
      </button>
    </div>
  );
}

/* ════════ FASE 3 — Telemetria ════════ */
function TelemetrySummary({ data }) {
  const d = data.device || {};
  const h = data.health || {};
  const hb = data.heartbeat || {};
  return (
    <div className="glass-card-pad space-y-2">
      <div className="text-xs font-semibold uppercase
                      tracking-wider text-noc-muted">
        Telemetria live
      </div>
      <TRow k="Heartbeat" v={fmtTime(d.last_seen)} />
      <TRow k="RSSI" v={d.rssi != null ? d.rssi + " dBm" : "—"}
            tone={d.rssi != null ? rssiTone(d.rssi) : ""} />
      <TRow k="Firmware" v={d.fw_version || "—"} />
      <TRow k="IP" v={d.ip_last || "—"} mono />
      <TRow k="Tempo online" v={fmtUptime(hb.uptime_s ?? h.uptime_s)} />
      <TRow k="Boot ID" v={d.boot_id || "—"} mono />
      <TRow k="Watchdog count" v={h.watchdog_count ?? "—"} />
      <TRow k="Reboot count" v={h.reboot_count ?? "—"} />
      <TRow k="Último reboot" v={d.last_boot_reason || "—"} />
      <TRow k="Canal seguro" v={
        <Badge tone={d.secure_channel ? "ok" : "muted"}>
          {d.secure_channel ? "HMAC ativo" : "sem HMAC"}
        </Badge>
      } />
      <TRow k="Versão config" v={d.config_revision ?? "—"} />
    </div>
  );
}

/* ════════ S7.5C-B — Fleet Health Card ════════
 *
 * Card "Saúde do Equipamento" — exibe os campos do firmware S7.5C-A
 * que já vêm no payload (data.heartbeat) mas ainda não aparecem na UI.
 *
 * Fonte de dados: data.heartbeat (= device.last_hb_payload, o JSONB
 * persistido a cada heartbeat). Devices com firmware antigo (< S7.5C-A)
 * têm esses campos ausentes — todas as leituras usam ?? "—".
 *
 * NÃO altera schema, endpoint ou heartbeat. Apenas renderiza dados
 * que já chegam.
 */
function FleetHealthCard({ data }) {
  const hb = data.heartbeat || {};
  const tH = (k) => t("deviceDetail.health." + k);

  const heapFree = hb.heap_free;
  const heapMin  = hb.heap_min;
  const rssiCur  = hb.rssi;
  const rssiMin  = hb.rssi_min;
  const lastOtaTs = hb.last_ota_confirm_time;

  return (
    <div className="glass-card-pad space-y-2">
      <div className="text-xs font-semibold uppercase
                      tracking-wider text-noc-muted">
        {tH("title")}
      </div>
      <TRow k={tH("bootCount")}    v={hb.boot_count ?? "—"} />
      <TRow k={tH("rebootReason")} v={hb.reboot_reason || "—"} />
      <TRow k={tH("heapFree")}     v={heapFree != null ? formatBytesKB(heapFree) : "—"} mono />
      <TRow k={tH("heapMin")}      v={heapMin != null ? formatBytesKB(heapMin) : "—"} mono />
      <TRow k={tH("rssiCurrent")}  v={rssiCur != null ? rssiCur + " dBm" : "—"}
            tone={rssiCur != null ? rssiTone(rssiCur) : ""} />
      <TRow k={tH("rssiMin")}      v={rssiMin != null && rssiMin !== 0 ? rssiMin + " dBm" : "—"}
            tone={rssiMin != null && rssiMin !== 0 ? rssiTone(rssiMin) : ""} />
      <TRow k={tH("wifiUptime")}   v={hb.wifi_uptime_s != null ? fmtUptime(hb.wifi_uptime_s) : "—"} />
      <TRow k={tH("wifiDisconnects")} v={hb.wifi_disconnects ?? "—"} />
      <TRow k={tH("lastOtaVersion")}  v={hb.last_ota_version || "—"} mono />
      <TRow k={tH("lastOtaConfirm")}  v={lastOtaTs ? fmtTime(lastOtaTs * 1000) : "—"} />
    </div>
  );
}

/* ════════ S7.6-A2.2 — Watchdog Runtime Card ════════
 *
 * Exibe o estado real do watchdog (bloco `watchdog_runtime`, firmware
 * S7.6-A2.2+). Lê o campo TIPADO data.watchdog_runtime (do /center) com
 * fallback ao cru data.heartbeat.watchdog_runtime. Firmware antigo ou
 * watchdog desabilitado (gate) → bloco ausente → "não reportado".
 */
function WatchdogCard({ data }) {
  const wr = data.watchdog_runtime || (data.heartbeat || {}).watchdog_runtime;
  if (!wr) {
    return (
      <div className="glass-card-pad space-y-2">
        <div className="text-xs font-semibold uppercase
                        tracking-wider text-noc-muted">Watchdog</div>
        <div className="text-xs text-noc-muted py-2">
          Watchdog não reportado (firmware antigo ou desabilitado).
        </div>
      </div>
    );
  }
  const hostRow = (h, ok) => (
    <span className="flex items-center gap-2 justify-end">
      <span className="font-mono">{h || "—"}</span>
      <Badge tone={ok ? "ok" : "warn"}>{ok ? "ok" : "falha"}</Badge>
    </span>
  );
  const cooldown = wr.cooldown_s != null
    ? fmtUptime(wr.cooldown_s)
      + (wr.cooldown_remaining_s > 0 ? ` (restam ${fmtUptime(wr.cooldown_remaining_s)})` : "")
    : "—";
  return (
    <div className="glass-card-pad space-y-2">
      <div className="text-xs font-semibold uppercase
                      tracking-wider text-noc-muted">Watchdog (runtime)</div>
      <TRow k="Estado" v={
        <Badge tone={wr.enabled ? "ok" : "muted"}>
          {wr.enabled ? "ativo" : "inativo"}
        </Badge>} />
      <TRow k="Modo / Ação" v={`${wr.mode || "—"} · ${wr.action || "—"}`} />
      <TRow k="Host 1" v={hostRow(wr.host1, wr.last_host1_ok)} />
      <TRow k="Host 2" v={hostRow(wr.host2, wr.last_host2_ok)} />
      <TRow k="Falhas" v={
        <Badge tone={(wr.failed_pings || 0) > 0 ? "warn" : "muted"}>
          {(wr.failed_pings ?? "—") + " / " + (wr.max_fails ?? "—")}
        </Badge>} />
      <TRow k="Intervalo" v={wr.interval_s != null ? wr.interval_s + "s" : "—"} />
      <TRow k="Janela timeout" v={wr.timeout_window_s != null ? wr.timeout_window_s + "s" : "—"} />
      <TRow k="Cooldown" v={cooldown} />
      <TRow k="Próx. checagem" v={wr.next_check_in_s != null ? wr.next_check_in_s + "s" : "—"} />
      <TRow k="Último disparo" v={wr.last_action_at ? fmtTime(wr.last_action_at * 1000) : "—"} />
      <TRow k="Motivo" v={wr.last_reason || "—"} mono />
    </div>
  );
}

/* ════════ S7.5C-C — Lista de Eventos OTA (histórico) ════════
 *
 * Histórico de eventos OTA via endpoint GET /api/admin/devices/{id}/
 * ota-events. Lê de heartbeats_raw.payload->'ota_events' no backend.
 *
 * Evolução desde S7.5C-B:
 *   - S7.5C-B lia hb.ota_events (snapshot em last_hb_payload), mas
 *     firmware drena a fila após 2xx → snapshot vazio quase sempre.
 *   - S7.5C-C lê do histórico bruto (heartbeats_raw) → vê OTAs passados.
 *
 * Backend deduplica por (event, ts, fw, progress, reason) — chave
 * uniforme. Limite default 50. Refetch quando deviceId ou reloadKey
 * mudam (mesma estratégia que listCommands no OtaTab).
 *
 * Formato do item: { event, fw, ts, uptime_s, received_at,
 *                    progress, reason, rssi, size }
 * (campos não aplicáveis vêm null — schema sem buracos).
 */
function OtaEventsList({ deviceId, reloadKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!deviceId) return;
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    listDeviceOtaEvents(deviceId, { limit: 50, signal: ac.signal })
      .then((res) => {
        setItems(Array.isArray(res?.items) ? res.items : []);
        setLoading(false);
      })
      .catch((e) => {
        if (e?.name === "AbortError") return;
        setError(e?.message || String(e));
        setLoading(false);
      });
    return () => ac.abort();
  }, [deviceId, reloadKey]);

  function eventTone(ev) {
    if (!ev) return "";
    if (ev === "OTA_FAILED") return "text-noc-err";
    if (ev === "OTA_CONFIRM_OK" || ev === "OTA_SUCCESS") return "text-noc-ok";
    return "";
  }

  function eventDetails(e) {
    const parts = [];
    if (e.progress != null) parts.push(e.progress + "%");
    if (e.size != null) parts.push(formatBytesKB(e.size));
    if (e.rssi != null) parts.push(e.rssi + " dBm");
    if (e.reason) parts.push(e.reason);
    return parts.join(" · ");
  }

  // Timestamp do evento: prioriza ts (epoch s do firmware); fallback
  // para received_at (ISO 8601 do backend) quando ts=0 (firmware sem NTP).
  function eventTime(e) {
    if (e.ts && e.ts > 0) return fmtTime(e.ts * 1000);
    if (e.received_at) return fmtTime(e.received_at);
    return "—";
  }

  return (
    <div className="glass-card overflow-hidden">
      <div className="px-3 py-2 text-xs font-semibold uppercase
                      tracking-wider text-noc-muted border-b
                      border-noc-border">
        {t("deviceDetail.health.otaEventsTitle")}
      </div>
      {loading ? (
        <div className="px-3 py-4 text-xs font-mono text-[12px] text-noc-faint">
          {t("deviceDetail.health.otaEventsLoading")}
        </div>
      ) : error ? (
        <div className="px-3 py-4 text-xs text-noc-err italic">
          {t("deviceDetail.health.otaEventsError").replace("{msg}", error)}
        </div>
      ) : items.length === 0 ? (
        <div className="px-3 py-4 text-xs font-mono text-[12px] text-noc-faint">
          {t("deviceDetail.health.otaEventsEmpty")}
        </div>
      ) : (
        <ul className="divide-y divide-noc-border/40">
          {items.map((e, i) => (
            <li key={i} className="px-3 py-2 flex items-center
                                  justify-between gap-3 text-xs">
              <div className="flex flex-col min-w-0">
                <span className={"font-mono font-semibold " + eventTone(e.event)}>
                  {e.event || "—"}
                </span>
                <span className="text-noc-muted/70 text-[10px]">
                  {e.fw || "—"} · uptime {fmtUptime(e.uptime_s ?? 0)}
                  {eventDetails(e) && <> · {eventDetails(e)}</>}
                </span>
              </div>
              <span className="text-noc-muted text-[10px] whitespace-nowrap">
                {eventTime(e)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TelemetryTab({ data }) {
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <TelemetrySummary data={data} />
      <FleetHealthCard data={data} />
      <WatchdogCard data={data} />
      {/* CONN-PROBE-3 — diagnóstico de conectividade (contrato tipado data.conn) */}
      <ConnProbeCard
        conn={data.conn}
        rssi={(data.heartbeat || {}).rssi}
        wifiCh={(data.heartbeat || {}).wifi_ch}
      />
      <div className="glass-card-pad">
        <div className="text-xs font-semibold uppercase
                        tracking-wider text-noc-muted mb-2">
          Heartbeat — payload bruto
        </div>
        <pre className="text-[11px] text-noc-muted font-mono
                        overflow-x-auto bg-noc-bg/50 rounded-lg
                        p-2 max-h-80">
{JSON.stringify(data.heartbeat || {}, null, 2)}
        </pre>
      </div>
      {/* CONN-PROBE-4-FE — histórico de conectividade (timeline LAN/INET/DNS),
          full-width logo abaixo do card de Conectividade. Lazy: só monta ao
          abrir a aba Telemetria. Read-only (heartbeats_raw). */}
      <div className="lg:col-span-3">
        <ConnHistoryPanel deviceId={data.device?.device_id} />
      </div>
    </div>
  );
}

function TRow({ k, v, mono, tone }) {
  return (
    <div className="flex items-center justify-between gap-3
                    text-xs border-b border-noc-border/40 pb-1.5">
      <span className="text-noc-muted">{k}</span>
      <span className={
        "font-medium text-right " + (mono ? "font-mono " : "")
        + (tone || "")
      }>
        {v}
      </span>
    </div>
  );
}

/* ════════ Sprint 6.5E — Localização real ════════ */
/**
 * Extrai lat/lng de um device a partir do objeto geo do
 * fleet-map. Tolera nomes alternativos. Retorna [lat,lng] ou null.
 */
function coordsOf(geo) {
  if (!geo) return null;
  const lat = geo.lat ?? geo.latitude
    ?? geo.last_lat ?? geo.last_location?.lat;
  const lng = geo.lng ?? geo.lon ?? geo.longitude
    ?? geo.last_lng ?? geo.last_location?.lng;
  if (lat == null || lng == null) return null;
  const a = Number(lat), b = Number(lng);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return [a, b];
}

/**
 * LeafletMap — mapa Leaflet reutilizável (mini ou grande).
 * Carrega o Leaflet via o hook useLeaflet (CDN, sem dep npm).
 * Renderiza um marker do device colorido por status.
 */
function LeafletMap({ coords, online, height = 130, zoom = 15 }) {
  const { L, ready, error } = useLeaflet();
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!ready || !L || !coords || !containerRef.current) return;
    // (re)inicializa o mapa
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: false,
      scrollWheelZoom: false,
    }).setView(coords, zoom);
    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/dark_all/"
      + "{z}/{x}/{y}{r}.png",
      { maxZoom: 19 }
    ).addTo(map);
    // marker colorido por status
    const color = online ? "#00c47f" : "#f94144";
    const icon = L.divIcon({
      className: "",
      html:
        `<div style="width:16px;height:16px;border-radius:50%;`
        + `background:${color};border:2px solid #0a1422;`
        + `box-shadow:0 0 12px ${color};"></div>`,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
    L.marker(coords, { icon }).addTo(map);
    mapRef.current = map;
    // garante o tamanho correto após render
    setTimeout(() => map.invalidateSize(), 100);
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [ready, L, coords?.[0], coords?.[1], online, zoom]);

  if (error) {
    return (
      <div className="rounded-lg border border-noc-border
                      bg-noc-bg/50 flex items-center justify-center
                      text-[11px] text-noc-muted/70"
           style={{ height }}>
        Falha ao carregar o mapa.
      </div>
    );
  }
  return (
    <div className="rounded-lg overflow-hidden border
                    border-noc-border relative"
         style={{ height }}>
      {!ready && (
        <div className="absolute inset-0 flex items-center
                        justify-center text-[11px]
                        text-noc-muted/70 bg-noc-bg/50 z-10">
          Carregando mapa…
        </div>
      )}
      <div ref={containerRef}
           style={{ height: "100%", width: "100%" }} />
    </div>
  );
}

/* ════════ FASE 5 — Mini-mapa (Cockpit) ════════ */
function MiniMap({ data, geo, deviceId, notify, onOpenMap }) {
  const inv = data.inventory || {};
  const d = data.device || {};
  const coords = coordsOf(geo);
  const online = isOnline(d.last_seen);

  function copyId() {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(deviceId)
        .then(() => notify && notify("ok", "device_id copiado."))
        .catch(() => notify && notify("error", "Falha ao copiar."));
    }
  }
  function openFleet() {
    // Etapa 4B — no cockpit o atalho leva à aba Localização (mapa
    // grande deste device). Sem onOpenMap mantém o aviso antigo.
    if (onOpenMap) { onOpenMap(); return; }
    notify && notify("ok",
      "Abra 'Fleet Map' no menu para o mapa completo.");
  }

  return (
    <div className="glass-card-pad space-y-2">
      <div className="flex items-center">
        <h2 className="section-label flex-1">{"Localização"}</h2>
        <button
          onClick={openFleet}
          className="font-mono text-[10px] tracking-[0.1em]
                     text-noc-accent hover:underline"
        >
          MAPA →
        </button>
      </div>

      {coords ? (
        <>
          <LeafletMap coords={coords} online={online}
                      height={140} />
          <div className="flex items-center gap-2">
            <Badge tone={online ? "ok" : "err"}>
              {online ? "online" : "offline"}
            </Badge>
            {d.rssi != null && (
              <Badge tone="muted">RSSI {d.rssi} dBm</Badge>
            )}
          </div>
          <TRow k="Latitude" v={coords[0].toFixed(6)} mono />
          <TRow k="Longitude" v={coords[1].toFixed(6)} mono />
          <TRow k="Última captura"
                v={fmtTime(geo?.last_seen || d.last_seen)} />
          <button
            onClick={openFleet}
            className="w-full h-8 rounded-lg text-xs font-semibold
                       border border-noc-accent/40 text-noc-accent
                       hover:bg-noc-accent/10 transition-colors"
          >
            Abrir no mapa
          </button>
        </>
      ) : (
        /* Estado vazio — sem coordenadas */
        <div className="rounded-lg border border-dashed
                        border-noc-border bg-noc-bg/40 p-4
                        text-center space-y-2">
          <div className="text-2xl opacity-40">🗺️</div>
          <div className="text-[11px] text-noc-muted
                          leading-relaxed">
            Sem coordenadas. Capture pelo App Técnico ou pelo
            Provisionamento.
          </div>
          <div className="flex flex-col gap-1.5 pt-1">
            <button
              onClick={openFleet}
              className="h-8 rounded-lg text-xs font-semibold
                         border border-noc-accent/40
                         text-noc-accent hover:bg-noc-accent/10
                         transition-colors"
            >
              Abrir mapa da frota
            </button>
            <button
              onClick={copyId}
              className="h-8 rounded-lg text-xs font-semibold
                         border border-noc-border text-noc-text
                         transition-colors"
            >
              Copiar device_id
            </button>
          </div>
        </div>
      )}
      <TRow k="Instalador" v={inv.installer || "—"} />
      <TRow k="Equipe" v={inv.team || "—"} />
    </div>
  );
}

/* ════════ FASE 5 — Aba Localização ════════ */
function LocationTab({ deviceId, data, geo, notify }) {
  const inv = data.inventory || {};
  const b = data.binding || {};
  const wo = data.work_order;
  const d = data.device || {};
  const coords = coordsOf(geo);
  const online = isOnline(d.last_seen);

  // origem da localização — inferida do que está disponível.
  const origin = locationOrigin(geo, inv, data);

  // histórico de localização — eventos da timeline que mencionam
  // geo/localização/captura.
  const locEvents = (data.timeline || []).filter((e) => {
    const txt = (
      (e.action || "") + (e.event || "") + (e.type || "")
    ).toLowerCase();
    return txt.includes("geo") || txt.includes("local")
      || txt.includes("captur") || txt.includes("posi");
  });

  function copyId() {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(deviceId)
        .then(() => notify && notify("ok", "device_id copiado."))
        .catch(() => {});
    }
  }

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {/* Coluna principal — mapa grande */}
      <div className="lg:col-span-2 space-y-3">
        <div className="glass-card-pad space-y-2">
          <div className="text-xs font-semibold uppercase
                          tracking-wider text-noc-muted">
            Localização do dispositivo
          </div>
          {coords ? (
            <>
              <LeafletMap coords={coords} online={online}
                          height={360} zoom={16} />
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={online ? "ok" : "err"}>
                  {online ? "online" : "offline"}
                </Badge>
                {d.rssi != null && (
                  <Badge tone="muted">
                    RSSI {d.rssi} dBm
                  </Badge>
                )}
                <span className="text-[11px] text-noc-muted
                                 font-mono ml-auto">
                  {coords[0].toFixed(6)}, {coords[1].toFixed(6)}
                </span>
              </div>
            </>
          ) : (
            <div className="rounded-lg border border-dashed
                            border-noc-border bg-noc-bg/40 p-8
                            text-center space-y-3">
              <div className="text-4xl opacity-40">🗺️</div>
              <div className="text-sm text-noc-muted">
                Sem coordenadas. Capture pelo App Técnico ou
                pelo Provisionamento.
              </div>
              <button
                onClick={copyId}
                className="h-9 px-4 rounded-lg text-sm
                           font-semibold border border-noc-border
                           text-noc-text"
              >
                Copiar device_id
              </button>
            </div>
          )}
        </div>

        {/* Histórico de localizações */}
        {locEvents.length > 0 && (
          <div className="glass-card-pad">
            <div className="text-xs font-semibold uppercase
                            tracking-wider text-noc-muted mb-2">
              Histórico de localização
            </div>
            <div className="space-y-1">
              {locEvents.map((e, i) => (
                <div key={i}
                     className="flex gap-3 text-xs items-start
                                border-l-2 border-noc-accent/40
                                pl-3 py-1">
                  <span className="text-noc-muted/70 shrink-0
                                   w-28">
                    {fmtTime(e.created_at || e.at)}
                  </span>
                  <span className="flex-1">
                    {e.action || e.event || "atualização"}
                    {e.notes ? " — " + e.notes : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Coluna lateral — detalhes */}
      <div className="space-y-3">
        <div className="glass-card-pad space-y-1.5">
          <div className="text-xs font-semibold uppercase
                          tracking-wider text-noc-muted">
            Detalhes
          </div>
          <TRow k="Latitude"
                v={coords ? coords[0].toFixed(6) : "—"} mono />
          <TRow k="Longitude"
                v={coords ? coords[1].toFixed(6) : "—"} mono />
          <TRow k="Cliente" v={b.customer_id || "—"} />
          <TRow k="Endereço"
                v={geo?.address || inv.location || "—"} />
          <TRow k="Instalador"
                v={inv.installer || wo?.installer || "—"} />
          <TRow k="Equipe" v={inv.team || "—"} />
          <TRow k="QR serial"
                v={inv.qr_serial || b.qr_serial || "—"} mono />
          <TRow k="Origem" v={origin} />
          <TRow k="Última captura"
                v={fmtTime(geo?.last_seen || d.last_seen)} />
        </div>
      </div>
    </div>
  );
}

/* Infere a origem da localização a partir dos dados. */
function locationOrigin(geo, inv, data) {
  if (geo && (geo.source || geo.origin)) {
    return geo.source || geo.origin;
  }
  // heurística: se há OS/inventory com instalador → App Técnico
  if (inv && inv.installer) return "App Técnico";
  if (data.work_order) return "Provisionamento";
  if (geo) return "Heartbeat";
  return "Manual";
}


/* ════════ FASE 6 — Cliente IXC ════════ */
function ClientCard({ data }) {
  const b = data.binding || {};
  const inv = data.inventory || {};
  const wo = data.work_order;
  // PROVISIONING-REPLACE-UX-2 — QR atual p/ a troca (binding ou estoque).
  const currentQr = b.qr_serial || inv.qr_serial || null;
  const deviceId = data.device?.device_id || b.device_id || null;
  const [replaceOpen, setReplaceOpen] = useState(false);
  return (
    <div className="glass-card-pad space-y-1.5">
      <h2 className="section-label mb-1 block">{"Cliente IXC"}</h2>
      <TRow k="Cliente" v={b.customer_id || "—"} />
      <TRow k="Contrato" v={b.contract_id || "—"} />
      <TRow k="Login PPPoE" v={b.pppoe_login || "—"} mono />
      <TRow k="RADIUS" v={b.radius_id || "—"} />
      <TRow k="QR serial" v={inv.qr_serial || b.qr_serial || "—"} mono />
      <TRow k="OS" v={
        wo ? String(wo.id).slice(0, 8) + " · " + wo.status : "—"
      } />
      <TRow k="Instalador" v={inv.installer || wo?.installer || "—"} />

      {/* PROVISIONING-REPLACE-UX-2 — Substituir equipamento */}
      <div className="pt-1.5">
        {currentQr ? (
          <button
            type="button"
            onClick={() => setReplaceOpen(true)}
            className="text-[10px] px-2 py-0.5 rounded border border-noc-warn/30 text-noc-warn hover:bg-noc-warn/10"
            title="Substituir equipamento (troca de Sonoff)"
          >
            🔁 Substituir equipamento
          </button>
        ) : (
          <span className="text-[10px] text-noc-muted/60 italic">
            Não foi possível localizar o vínculo/QR atual deste dispositivo.
          </span>
        )}
      </div>

      {replaceOpen && currentQr && (
        <ReplaceDeviceModal
          binding={{ qr_serial: currentQr, device_id: deviceId }}
          onClose={() => setReplaceOpen(false)}
          onReplaced={() => window.location.reload()}
        />
      )}
    </div>
  );
}

/* ════════ FASE 7 — Saúde (gauges) ════════ */
function ClientHealthTab({ data, deviceId, notify, onAfterAction }) {
  const h = data.health || {};
  const d = data.device || {};
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {/* DEVICE-DETAIL-MANAGE-1F — vínculo e autorização (full-width) */}
      <div className="lg:col-span-3">
        <BindingAuthCard data={data} notify={notify} onChanged={onAfterAction} />
      </div>
      {/* PROTECTED-DEVICES-MANAGE-1D — proteção dinâmica (full-width) */}
      <div className="lg:col-span-3">
        <DeviceProtectionCard data={data} notify={notify} onChanged={onAfterAction} />
      </div>
      <div className="lg:col-span-2 glass-card-pad">
        <div className="text-xs font-semibold uppercase
                        tracking-wider text-noc-muted mb-3">
          Saúde do dispositivo
        </div>
        <div className="flex items-center gap-3 mb-4">
          <Badge tone={healthTone(h.health_status)}>
            {(h.health_status || "desconhecido").toUpperCase()}
          </Badge>
          <span className="text-xs text-noc-muted">
            Estado consolidado da saúde
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Gauge label="RSSI"
                 value={d.rssi}
                 unit="dBm"
                 pct={rssiPct(d.rssi)} />
          <Gauge label="Heartbeat"
                 value={isOnline(d.last_seen) ? "OK" : "OFF"}
                 pct={isOnline(d.last_seen) ? 100 : 0} />
          <Gauge label="Watchdog"
                 value={h.watchdog_count ?? 0}
                 pct={countPct(h.watchdog_count, 10)}
                 invert />
          <Gauge label="Reboots"
                 value={h.reboot_count ?? 0}
                 pct={countPct(h.reboot_count, 10)}
                 invert />
          <Gauge label="OTA falhas"
                 value={countOtaFails(data)}
                 pct={countPct(countOtaFails(data), 5)}
                 invert />
        </div>
        <ScoreExplain deviceId={deviceId} />
      </div>
      <div>
        <ClientCard data={data} />
        <div className="mt-4">
          <AlertsMini
            data={data} notify={notify}
            onAfterAction={onAfterAction}
          />
        </div>
      </div>
    </div>
  );
}

/* S7.8.3 — Health Explainability: "Por que este score?" (read-only).
   Busca /health/{id}/explain; esconde a seção em 404/erro (flag off etc). */
const SEV_HEX = { info: "#94a3b8", warning: "#f5a400", critical: "#f94144" };

function ScoreExplain({ deviceId }) {
  const [exp, setExp] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!deviceId) return;
    const ac = new AbortController();
    healthExplain(deviceId, { signal: ac.signal })
      .then((r) => { setExp(r); setHidden(false); })
      .catch((e) => { if (e?.name !== "AbortError") setHidden(true); });
    return () => ac.abort();
  }, [deviceId]);

  if (hidden || !exp) return null;
  const bd = exp.score_breakdown || {};
  const ra = exp.recent_activity_24h || {};
  const penalties = (bd.penalties || []).filter((p) => p.penalty > 0);

  return (
    <div className="mt-4 pt-3 border-t border-noc-border/60">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wider
                        text-noc-muted">
          Por que este score?
        </div>
        <div className="text-sm font-bold"
             style={{ color: SEV_HEX[exp.reasons?.[0]?.severity] || "#cbd5e1" }}>
          {exp.health_score}
        </div>
      </div>

      {/* Decomposição do score (fiel) */}
      {penalties.length === 0 ? (
        <div className="text-[11px] text-noc-ok">
          Sem penalidades — saúde plena.
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {penalties.map((p) => (
            <span key={p.dimension}
                  className="text-[10px] px-1.5 py-0.5 rounded border"
                  style={{
                    color: SEV_HEX[p.severity] || "#cbd5e1",
                    borderColor: (SEV_HEX[p.severity] || "#cbd5e1") + "66",
                  }}
                  title={p.caveat || ""}>
              {p.dimension} {p.severity} −{p.penalty}
              {p.value ? ` · ${p.value}` : ""}
            </span>
          ))}
        </div>
      )}

      {/* Motivos legíveis */}
      {(exp.reasons || []).length > 0 && (
        <ul className="text-[11px] text-noc-muted space-y-0.5 mb-2">
          {exp.reasons.map((r) => (
            <li key={r.code}>
              <span style={{ color: SEV_HEX[r.severity] || "#cbd5e1" }}>•</span>{" "}
              <span className="text-noc-text">{r.label}</span>
              {r.detail ? ` — ${r.detail}` : ""}
            </li>
          ))}
        </ul>
      )}

      {/* Atividade real nas últimas 24h (device_events) */}
      <div className="text-[10px] text-noc-muted/80">
        Atividade 24h (eventos reais):{" "}
        <span className="text-noc-text">{ra.watchdog_action || 0}</span> watchdog ·{" "}
        <span className="text-noc-text">{ra.device_reboot || 0}</span> reboots ·{" "}
        <span className="text-noc-text">{ra.device_boot || 0}</span> boots
      </div>
    </div>
  );
}

function Gauge({ label, value, unit, pct, invert }) {
  const p = Math.max(0, Math.min(100, pct || 0));
  // invert: para contadores, mais alto = pior.
  const good = invert ? p < 40 : p > 60;
  const mid = invert ? p < 70 : p > 30;
  const color = good ? "#00c47f" : mid ? "#f5a400" : "#f94144";
  return (
    <div className="rounded-lg border border-noc-border
                    bg-noc-panel2/40 p-3">
      <div className="font-mono text-[9.5px] uppercase tracking-[0.14em]
                      text-noc-muted mb-2">
        {label}
      </div>
      <div className="relative h-2 rounded-full bg-noc-bg
                      overflow-hidden">
        <div className="h-full rounded-full transition-all"
             style={{ width: p + "%", background: color }} />
      </div>
      <div className="text-sm font-bold mt-1.5">
        {value ?? "—"}{unit ? " " + unit : ""}
      </div>
    </div>
  );
}

/* ════════ FASE 4 — Timeline operacional ════════ */
function TimelineTab({ data }) {
  const events = data.timeline || [];
  if (!events.length) {
    return (
      <div className="glass-card-pad text-sm text-noc-muted/70
                      italic">
        Sem eventos na timeline operacional.
      </div>
    );
  }
  return (
    <div className="glass-card-pad">
      <div className="text-xs font-semibold uppercase
                      tracking-wider text-noc-muted mb-3">
        Timeline operacional
      </div>
      <div className="space-y-1">
        {events.map((e, i) => (
          <div key={i}
               className="flex gap-3 text-xs items-start
                          border-l-2 border-noc-accent/40 pl-3
                          py-1.5 hover:bg-noc-panel2/30
                          transition-colors rounded-r">
            <span className="text-noc-muted/70 shrink-0 w-28">
              {fmtTime(e.created_at || e.at || e.timestamp)}
            </span>
            <span className="flex-1 text-noc-text">
              {e.action || e.event || e.type || "evento"}
              {e.notes ? " — " + e.notes : ""}
            </span>
            <span className="text-noc-muted shrink-0">
              {e.actor || e.user || "sistema"}
            </span>
            {e.result && (
              <Badge tone={
                e.result === "ok" || e.result === "success"
                  ? "ok" : "muted"
              }>
                {e.result}
              </Badge>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertsMini({ data, notify, onAfterAction }) {
  const alerts = (data.alerts || []).filter(
    (a) => a.status === "open"
  );
  const [busy, setBusy] = useState(null);

  async function act(id, action) {
    setBusy(id + action);
    try {
      await ackAlert(id, action, "noc");
      notify("ok", action === "close"
        ? "Alerta fechado." : "Alerta reconhecido.");
      onAfterAction && onAfterAction();
    } catch (e) {
      notify("error", `Falha: ${e?.message || "erro"}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="glass-card-pad space-y-2">
      <div className="text-xs font-semibold uppercase
                      tracking-wider text-noc-muted">
        Alertas abertos
      </div>
      {alerts.length === 0 ? (
        <div className="text-xs font-mono text-[12px] text-noc-faint">
          Nenhum alerta aberto.
        </div>
      ) : (
        alerts.map((a) => (
          <div key={a.id}
               className="rounded-lg border border-noc-border
                          bg-noc-panel2/40 p-2 space-y-1">
            <div className="flex items-center gap-2">
              <Badge tone={sevTone(a.severity)}>
                {a.severity}
              </Badge>
              <span className="text-xs flex-1 truncate">
                {a.rule || "alerta"}
              </span>
            </div>
            <div className="flex gap-1.5">
              <button
                onClick={() => act(a.id, "ack")}
                disabled={busy !== null}
                className="text-[11px] px-2 py-0.5 rounded border
                           border-noc-border text-noc-text
                           disabled:opacity-40"
              >
                Reconhecer
              </button>
              <button
                onClick={() => act(a.id, "close")}
                disabled={busy !== null}
                className="text-[11px] px-2 py-0.5 rounded border
                           border-noc-accent/40 text-noc-accent
                           disabled:opacity-40"
              >
                Fechar
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/* ════════ Sprint 6.5C — Aba Comandos ════════ */
/**
 * Aba Comandos — histórico operacional restaurado no Device
 * Center v3. Consome GET /devices/{id}/commands (CommandOut
 * completo: payload, result, created_by, delivered/acked).
 *
 * Ações: reenviar (re-enqueue via POST), copiar payload
 * (client-side). "Cancelar pendente" NÃO tem endpoint no backend
 * — fica desabilitado com aviso (não inventamos API).
 */
function CommandsTab({ deviceId, notify, otaProtected, refreshKey }) {
  const confirmCritical = useCriticalConfirm(); // S3.10A
  const [cmds, setCmds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listCommands(deviceId, { limit: 100 })
      .then((res) => {
        if (!alive) return;
        // o endpoint devolve uma lista de CommandOut
        const list = Array.isArray(res)
          ? res
          : (res?.commands || res?.items || []);
        setCmds(list);
        setErr(null);
      })
      .catch((e) => {
        if (alive) setErr(e?.message || "Falha ao carregar.");
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // refreshKey vem do reload() da página → refetch quando um comando é
    // disparado no CommandGrid/QuickActions (antes a lista não atualizava).
  }, [deviceId, reloadKey, refreshKey]);

  function reload() { setReloadKey((k) => k + 1); }

  // Filtros — categorias do briefing.
  const filtered = cmds.filter((c) => {
    if (filter === "all") return true;
    if (filter === "pending") return c.status === "pending";
    if (filter === "acked") return c.status === "acked";
    if (filter === "failed") return c.status === "failed";
    if (filter === "ota") return c.type === "ota_update";
    if (filter === "energy") {
      return ["relay_on", "relay_off", "relay_reboot"]
        .includes(c.type);
    }
    if (filter === "system") {
      return ["ping", "reboot"].includes(c.type);
    }
    if (filter === "automation") {
      return (c.created_by || "").includes("auto")
        || (c.rollout_batch_id != null);
    }
    return true;
  });

  // Painel lateral — estatísticas.
  const stats = computeCmdStats(cmds);

  async function resend(c) {
    // S3.10A — reenvio de comando crítico também exige confirmação forte.
    // NOC-DEVICE-DETAIL-UX — relay_on é tão impactante quanto relay_off (mesma
    // política do CommandGrid), então também pede confirmação forte no reenvio.
    const critical = ["relay_on", "relay_off", "relay_reboot", "reboot"]
      .includes(c.type);
    let confirm;
    if (critical) {
      const secret = await confirmCritical({
        actionLabel: `Reenviar "${c.type}"`,
        target: { kind: "single", deviceId },
        impact: "Reenvia um comando crítico ao equipamento em campo.",
      });
      if (secret == null) return;
      confirm = secret;
    } else {
      // não-crítico (ex.: ping): confirma; em device sensível, avisa qual é.
      const s = deviceSensitivity(deviceId, otaProtected);
      const extra = s.sensitive ? `\n\nDispositivo: ${s.label}.` : "";
      if (!window.confirm(`Reenviar o comando "${c.type}"?${extra}`)) return;
    }
    setBusy(c.id + "resend");
    try {
      await enqueueSimpleCommand(deviceId, c.type, { confirm });
      notify("ok", `Comando "${c.type}" reenviado.`);
      reload();
    } catch (e) {
      notify("error",
        `Reenvio falhou: ${e?.message || "erro"}`);
    } finally {
      setBusy(null);
    }
  }

  function copyPayload(c) {
    const txt = JSON.stringify(c.payload || {}, null, 2);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(txt)
        .then(() => notify("ok", "Payload copiado."))
        .catch(() => notify("error", "Falha ao copiar."));
    } else {
      notify("error", "Clipboard indisponível.");
    }
  }

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {/* Coluna principal — tabela */}
      <div className="lg:col-span-2 space-y-3">
        {/* Filtros */}
        <div className="flex flex-wrap gap-1.5">
          {CMD_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={
                "text-xs px-2.5 py-1 rounded-lg border "
                + "transition-colors "
                + (filter === f.id
                    ? "border-noc-accent text-noc-accent "
                      + "bg-noc-accent/10"
                    : "border-noc-border text-noc-muted "
                      + "hover:text-noc-text")
              }
            >
              {f.label}
            </button>
          ))}
        </div>

        {err && (
          <div className="text-sm text-noc-err bg-noc-err/10
                          border border-noc-err/30 rounded-lg p-3">
            {err}
          </div>
        )}
        {loading ? (
          <div className="text-sm font-mono text-[12px] text-noc-faint">
            Carregando comandos…
          </div>
        ) : filtered.length === 0 ? (
          <div className="glass-card-pad text-sm
                          font-mono text-[12px] text-noc-faint">
            Nenhum comando para este filtro.
          </div>
        ) : (
          <div className="glass-card overflow-hidden">
            {/* cabeçalho da tabela */}
            <div className="grid grid-cols-12 gap-2 px-3 py-2
                            font-mono text-[9.5px] uppercase tracking-[0.14em]
                            text-noc-muted border-b
                            border-noc-border">
              <div className="col-span-3">Data/Hora</div>
              <div className="col-span-3">Comando</div>
              <div className="col-span-2">Origem</div>
              <div className="col-span-2">Usuário</div>
              <div className="col-span-2">Status</div>
            </div>
            {filtered.map((c) => (
              <CommandRow
                key={c.id}
                cmd={c}
                otaProtected={otaProtected}
                expanded={expanded === c.id}
                onToggle={() =>
                  setExpanded(expanded === c.id ? null : c.id)}
                onResend={() => resend(c)}
                onCopy={() => copyPayload(c)}
                busy={busy}
              />
            ))}
          </div>
        )}
      </div>

      {/* Painel lateral — estatísticas */}
      <div className="space-y-3">
        <div className="glass-card-pad space-y-2">
          <div className="text-xs font-semibold uppercase
                          tracking-wider text-noc-muted">
            Resumo de comandos
          </div>
          <TRow k="Último comando" v={stats.lastCmd} />
          <TRow k="Último ACK" v={stats.lastAck} />
          <TRow k="Fila pendente" v={stats.pending}
                tone={stats.pending > 0 ? "text-noc-warn" : ""} />
          {/* não é latência de rede: inclui fila + ciclo de heartbeat do device. */}
          <TRow k="Tempo até ACK (méd.)" v={stats.avgAck} />
          <TRow k="Comandos 24h" v={stats.last24h} />
          <TRow k="Falhas" v={stats.failed}
                tone={stats.failed > 0 ? "text-noc-err" : ""} />
        </div>
        <div className="text-[10px] text-noc-muted/70
                        leading-relaxed glass-card-pad">
          "Cancelar pendente" não tem endpoint no backend e fica
          desabilitado. Reenviar cria um novo comando do mesmo
          tipo (re-enqueue). Copiar payload é local.
        </div>
      </div>
    </div>
  );
}

const CMD_FILTERS = [
  { id: "all", label: "Todos" },
  { id: "pending", label: "Pendentes" },
  { id: "acked", label: "ACK" },
  { id: "failed", label: "Falhou" },
  { id: "ota", label: "Atualizações" },
  { id: "energy", label: "Energia" },
  { id: "system", label: "Sistema" },
  { id: "automation", label: "Automação" },
];

function CommandRow({
  cmd, otaProtected, expanded, onToggle, onResend, onCopy, busy,
}) {
  const c = cmd;
  // Reenviar usa enqueueSimpleCommand (payload vazio) → só faz sentido p/ comandos
  // simples sem payload. relay_reboot exige duration_s (422), ota_update é rejeitado
  // pela rota (422), config_* re-aplicaria config (drift). Em device SENSÍVEL
  // (produção protegida OU unidade-ouro/referência), reenvio de comando mutante é
  // bloqueado na UI (produção: backend também barra 403; gold: atrito anti-acidente).
  const RESENDABLE = ["ping", "reboot", "relay_on", "relay_off"];
  const resendUnsupported = !RESENDABLE.includes(c.type);
  const sens = deviceSensitivity(c.device_id, otaProtected);
  const resendBlockedSensitive = sens.sensitive && c.type !== "ping";
  const resendDisabled =
    busy !== null || resendUnsupported || resendBlockedSensitive;
  const resendTitle = resendUnsupported
    ? "Reenviar indisponível para este tipo de comando"
    : resendBlockedSensitive
      ? `Dispositivo sensível (${sens.label}) — reenvio de comando mutante `
        + "bloqueado na UI"
      : "Cria um novo comando do mesmo tipo";
  return (
    <div className="border-b border-noc-border/40 last:border-0">
      {/* linha */}
      <button
        onClick={onToggle}
        className="w-full grid grid-cols-12 gap-2 px-3 py-2
                   text-xs items-center text-left
                   hover:bg-noc-panel2/40 transition-colors"
      >
        <div className="col-span-3 text-noc-muted">
          {fmtTime(c.created_at)}
        </div>
        <div className="col-span-3 font-mono font-medium" title={c.type}>
          {commandTypeLabel(c.type)}
        </div>
        <div className="col-span-2 text-noc-muted">
          {cmdOrigin(c)}
        </div>
        <div className="col-span-2 text-noc-muted truncate">
          {c.created_by || "—"}
        </div>
        <div className="col-span-2">
          <Badge tone={cmdStatusTone(c.status)} title={c.status}>
            {commandStatusLabel(c.status)}
          </Badge>
        </div>
      </button>

      {/* item expandido */}
      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-2 bg-noc-bg/40">
          <div className="grid sm:grid-cols-2 gap-2 text-[11px]">
            <ExpRow k="Command ID" v={c.id} mono />
            <ExpRow k="Criado em" v={fmtTime(c.created_at)} />
            <ExpRow k="Entregue em" v={fmtTime(c.delivered_at)} />
            <ExpRow k="ACK em" v={fmtTime(c.acked_at)} />
            <ExpRow k="Tempo execução"
                    v={cmdExecTime(c)} />
            <ExpRow k="Expira em" v={fmtTime(c.expires_at)} />
          </div>
          <ExpJson label="Payload" value={c.payload} />
          {/* ping/relay/reboot só dão ACK (sem result); 'vazio' parecia falha.
              Mostra confirmação explícita quando o device confirmou (acked). */}
          <ExpJson label="Response / ACK" value={c.result}
            emptyHint={c.status === "acked"
              ? "Confirmado pelo device (sem payload de resposta)"
              : undefined} />
          <div className="flex gap-2 pt-1">
            <Can capability={CAP.DEVICES_COMMAND}>
              <button
                onClick={onResend}
                disabled={resendDisabled}
                title={resendTitle}
                className="text-[11px] px-2.5 py-1 rounded border
                           border-noc-accent/40 text-noc-accent
                           disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy === c.id + "resend" ? "…" : "Reenviar"}
              </button>
            </Can>
            <button
              onClick={onCopy}
              className="text-[11px] px-2.5 py-1 rounded border
                         border-noc-border text-noc-text"
            >
              Copiar payload
            </button>
            {c.status === "pending" && (
              <button
                disabled
                title="Cancelar comando não tem endpoint no backend"
                className="text-[11px] px-2.5 py-1 rounded border
                           border-noc-border/50 text-noc-muted/50
                           cursor-not-allowed"
              >
                Cancelar pendente ⚠
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ExpRow({ k, v, mono }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-noc-muted">{k}</span>
      <span className={
        "text-right truncate " + (mono ? "font-mono" : "")
      }>
        {v ?? "—"}
      </span>
    </div>
  );
}

function ExpJson({ label, value, emptyHint }) {
  const txt = value && Object.keys(value).length
    ? JSON.stringify(value, null, 2)
    : null;
  return (
    <div>
      <div className="font-mono text-[9.5px] uppercase tracking-[0.14em]
                      text-noc-muted mb-1">
        {label}
      </div>
      {txt ? (
        <pre className="text-[10px] text-noc-muted font-mono
                        bg-noc-bg/60 rounded-lg p-2
                        overflow-x-auto max-h-40">
{txt}
        </pre>
      ) : emptyHint ? (
        <div className="text-[11px] text-noc-ok">✓ {emptyHint}</div>
      ) : (
        <div className="text-[11px] text-noc-muted/60 italic">
          vazio
        </div>
      )}
    </div>
  );
}

function cmdOrigin(c) {
  if (c.rollout_batch_id != null) return "rollout";
  const by = (c.created_by || "").toLowerCase();
  if (by.includes("auto") || by.includes("watchdog")) {
    return "automação";
  }
  if (by.includes("system")) return "sistema";
  return "manual";
}

function cmdStatusTone(s) {
  if (s === "acked") return "ok";
  if (s === "failed" || s === "expired") return "err";
  if (s === "delivered") return "warn";
  return "muted";
}

function cmdExecTime(c) {
  if (!c.created_at || !c.acked_at) return "—";
  const a = new Date(c.created_at).getTime();
  const b = new Date(c.acked_at).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return "—";
  const s = Math.round((b - a) / 1000);
  return s < 60 ? s + "s" : Math.round(s / 60) + "min";
}

function computeCmdStats(cmds) {
  const sorted = [...cmds].sort((a, b) =>
    new Date(b.created_at) - new Date(a.created_at));
  const pending = cmds.filter(
    (c) => c.status === "pending").length;
  const failed = cmds.filter(
    (c) => c.status === "failed").length;
  const dayAgo = Date.now() - 24 * 3600 * 1000;
  const last24h = cmds.filter(
    (c) => new Date(c.created_at).getTime() > dayAgo).length;
  // tempo ACK médio
  const acked = cmds.filter((c) => c.created_at && c.acked_at);
  let avgAck = "—";
  if (acked.length) {
    const total = acked.reduce((sum, c) => {
      const d = new Date(c.acked_at) - new Date(c.created_at);
      return sum + (d > 0 ? d : 0);
    }, 0);
    const s = Math.round(total / acked.length / 1000);
    avgAck = s < 60 ? s + "s" : Math.round(s / 60) + "min";
  }
  const lastAcked = sorted.find((c) => c.acked_at);
  return {
    lastCmd: sorted[0]
      ? sorted[0].type + " · " + fmtTime(sorted[0].created_at)
      : "—",
    lastAck: lastAcked
      ? lastAcked.type + " · " + fmtTime(lastAcked.acked_at)
      : "—",
    pending, failed, last24h, avgAck,
  };
}

/* ════════ Sprint 6.5D — Aba OTA (cockpit OTA) ════════ */
/**
 * Aba OTA — cockpit operacional de firmware por dispositivo.
 *
 * Consome endpoints existentes:
 *   - /center            → firmware atual, releases, rollouts;
 *   - listFirmwareReleases / getFirmwareRelease → notas/SHA/tam;
 *   - listCommands       → histórico de ota_update;
 *   - triggerOta         → disparar/repetir/rollback OTA;
 *   - heartbeat bruto    → campos ota_* (diagnóstico).
 *
 * NÃO cria APIs. "Cancelar OTA pendente" não tem endpoint de
 * cancelamento — fica desabilitado com aviso.
 */
function OtaTab({ deviceId, data, notify, onAfterAction }) {
  const confirmCritical = useCriticalConfirm(); // S3.10A
  const [releases, setReleases] = useState([]);
  const [otaCmds, setOtaCmds] = useState([]);
  const [selRelease, setSelRelease] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // NOC-OTA-COMPAT-GUARD — frase digitada p/ liberar salto com risco de reset.
  const [resetAck, setResetAck] = useState("");

  const hb = data.heartbeat || {};
  const d = data.device || {};
  const fw = data.firmware || {};

  // NOC-OTA-COMPAT-GUARD — fw atual do device + compat da release selecionada.
  const deviceFw = fw.current || hb.fw || d.fw_version || null;
  // Releases FE-dev/factory NÃO são opção de OTA app (full-flash pelo Tools) → ocultas.
  const appReleases = releases.filter(
    (r) => !classifyRelease(r.version, r.notes, r.kind).factory_tools_only
  );
  const hiddenFactory = releases.length - appReleases.length;
  // HARDEN-1 — contexto do device p/ o guard espelhar o backend: suporte a OTA-FS
  // (heartbeat traz fs_version), proteção efetiva e se o operador é platform admin.
  const fsSupported = !!(hb && hb.fs_version && hb.fs_version !== "--");
  const compatOpts = {
    confirmConfigReset: resetAck.trim().toUpperCase() === RESET_CONFIRM_PHRASE,
    fsSupported,
    isProtected: getProtectionState(data).protected,
    isPlatformAdmin: isPlatformAdmin(),
  };
  const compat = selRelease ? evaluateOtaCompat(deviceFw, selRelease, compatOpts) : null;
  // Só o risco LEVE (needs_reset_confirm) usa a frase; block é bloqueio duro.
  const needsPhrase = compat ? compat.decision === "needs_reset_confirm" : false;
  const phraseOk = resetAck.trim().toUpperCase() === RESET_CONFIRM_PHRASE;
  const compatBlocks = compat && compat.decision === "block";

  useEffect(() => {
    let alive = true;
    // releases disponíveis (SÓ kind=app — o OTA de app nunca aceita release de
    // filesystem; kind=fs vai na seção "Atualização de Filesystem") + histórico OTA.
    Promise.allSettled([
      listFirmwareReleases({ kind: "app" }),
      listCommands(deviceId, { limit: 100 }),
    ]).then(([rel, cmds]) => {
      if (!alive) return;
      if (rel.status === "fulfilled") {
        const list = Array.isArray(rel.value)
          ? rel.value
          : (rel.value?.releases || rel.value?.items || []);
        setReleases(list);
      }
      if (cmds.status === "fulfilled") {
        const list = Array.isArray(cmds.value)
          ? cmds.value
          : (cmds.value?.commands || cmds.value?.items || []);
        setOtaCmds(
          list.filter((c) => c.type === "ota_update")
        );
      }
    });
    return () => { alive = false; };
  }, [deviceId, reloadKey]);

  function reload() {
    setReloadKey((k) => k + 1);
    onAfterAction && onAfterAction();
  }

  // Carrega detalhe de uma release ao selecionar.
  async function pickRelease(id) {
    if (!id) { setSelRelease(null); return; }
    try {
      const full = await getFirmwareRelease(id);
      setSelRelease(full);
    } catch {
      // fallback: usa o que veio da lista
      setSelRelease(releases.find((r) => r.id === id) || null);
    }
  }

  async function doOta(label, releaseId, danger = true) {
    if (!releaseId) {
      notify("error", "Selecione uma versão.");
      return;
    }
    // NOC-OTA-COMPAT-GUARD — o backend é a autoridade (recusa 409/412), mas gateamos
    // aqui p/ dar feedback claro e exigir a frase antes de disparar.
    const rel = selRelease && selRelease.id === releaseId
      ? selRelease : releases.find((r) => r.id === releaseId);
    const confirmReset = resetAck.trim().toUpperCase() === RESET_CONFIRM_PHRASE;
    const ev = evaluateOtaCompat(deviceFw, rel, { ...compatOpts, confirmConfigReset: confirmReset });
    if (ev.decision === "block") {
      // HARDEN-1 — bloqueio duro (migração perigosa/factory/comercial/protegido):
      // sem contorno pela UI; use bancada/full-flash. O backend também recusa 409.
      notify("error", ev.message);
      return;
    }
    if (ev.decision === "needs_reset_confirm") {
      notify("error", `Confirme digitando a frase exata: "${RESET_CONFIRM_PHRASE}".`);
      return;
    }
    const _secret = await confirmCritical({
      actionLabel: `${label} (OTA)`,
      target: { kind: "single", deviceId },
      impact:
        (confirmReset ? "⚠ Pode RESETAR a config (Wi-Fi/watchdog/agenda). " : "") +
        "O equipamento vai BAIXAR, FLASHAR e REINICIAR durante a atualização OTA.",
    });
    if (_secret == null) return;
    setBusy(label);
    try {
      await triggerOta(deviceId, releaseId, { confirm: _secret, confirm_config_reset: confirmReset });
      notify("ok", `${label} — OTA enfileirado.`);
      setResetAck("");
      reload();
    } catch (e) {
      // 409/412 do guard trazem motivo estruturado em err.body.detail.
      const msg = e?.body?.detail?.message || e?.message || "erro";
      notify("error", `${label} falhou: ${msg}`);
    } finally {
      setBusy(null);
    }
  }

  // último comando OTA — base do progresso/logs
  const lastOta = [...otaCmds].sort((a, b) =>
    new Date(b.created_at) - new Date(a.created_at))[0];
  const otaState = otaStateOf(hb, lastOta);

  return (
    <div className="space-y-4">
      {/* PROTECTED-DEVICES-MANAGE-1E — aviso de proteção EFETIVA (ENV OR DB) na aba
          OTA. O backend bloqueia (403); aqui sinalizamos ANTES do submit. */}
      {getProtectionState(data).protected && (
        <div className="text-[12px] text-violet-200 bg-violet-400/10 border
                        border-violet-400/40 rounded p-2.5 flex items-start gap-2">
          <span aria-hidden>🔒</span>
          <span>
            <b>Dispositivo protegido.</b> OTA e Rollback são
            <b> bloqueados pelo backend</b>. Use um device de bancada para testes.
          </span>
        </div>
      )}
      {/* 1 — STATUS ATUAL */}
      <div className="glass-card-pad">
        <div className="text-xs font-semibold uppercase
                        tracking-wider text-noc-muted mb-3">
          Status OTA
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4
                        lg:grid-cols-8 gap-2">
          <OtaStat label="Firmware" value={fw.current || "—"} />
          <OtaStat label="Config" value={
            d.config_revision != null
              ? "v" + d.config_revision : "—"} />
          <OtaStat label="OTA status" value={otaState.label}
                   tone={otaState.tone} />
          <OtaStat label="Último OTA"
                   value={lastOta
                     ? fmtAgo(lastOta.created_at) : "—"} />
          <OtaStat label="Rollouts"
                   value={(data.rollouts || []).length} />
          <OtaStat label="Falhas OTA"
                   value={otaCmds.filter(
                     (c) => c.status === "failed").length}
                   tone={otaCmds.some(
                     (c) => c.status === "failed")
                     ? "err" : "muted"} />
          <OtaStat label="Canal"
                   value={d.secure_channel ? "seguro" : "aberto"}
                   tone={d.secure_channel ? "ok" : "muted"} />
          <OtaStat label="Desde atualização"
                   value={hb.ota_last_success
                     ? fmtAgo(hb.ota_last_success)
                     : (lastOta && lastOta.status === "acked"
                         ? fmtAgo(lastOta.acked_at) : "—")} />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* 3 — DISPARO OTA */}
        <div className="glass-card-pad space-y-2">
          <div className="text-xs font-semibold uppercase
                          tracking-wider text-noc-muted">
            Disparar OTA — firmware (app)
          </div>
          {releases.length === 0 ? (
            <div className="text-xs font-mono text-[12px] text-noc-faint">
              Nenhuma release de firmware (kind=app) disponível.
            </div>
          ) : (
            <>
              <select
                className="installer-input"
                value={selRelease?.id || ""}
                onChange={(e) => { pickRelease(e.target.value); setResetAck(""); }}
              >
                <option value="">Selecione a versão…</option>
                {appReleases.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.version}
                    {r.deprecated ? " (obsoleta)" : ""}
                  </option>
                ))}
              </select>
              {hiddenFactory > 0 && (
                <div className="text-[10px] text-noc-muted/70">
                  🔒 {hiddenFactory} release(s) FE-dev/factory ocultada(s) — são full-flash
                  (5 bins) pelo Tools, <b>não</b> OTA app.
                </div>
              )}
              {selRelease && (
                <div className="space-y-1 pt-1 text-[11px]">
                  <ExpRow k="Versão" v={selRelease.version} />
                  <ExpRow k="Tamanho"
                          v={fmtBytes(selRelease.size_bytes)} />
                  <ExpRow k="SHA-256"
                          v={shortSha(selRelease.sha256)} mono />
                  <ExpRow k="Criado em"
                          v={fmtTime(selRelease.created_at)} />
                  {selRelease.notes && (
                    <div className="text-noc-muted pt-1">
                      {selRelease.notes}
                    </div>
                  )}
                </div>
              )}
              {/* NOC-OTA-COMPAT-GUARD — painel de compatibilidade (fw atual→alvo, tipo,
                  risco de reset, e frase de confirmação quando aplicável). */}
              {selRelease && compat && (
                <OtaCompatPanel compat={compat} deviceFw={deviceFw} rel={selRelease}
                                resetAck={resetAck} setResetAck={setResetAck} />
              )}
              {/* FW-OTA-MIGRATOR-BRIDGE — plano de atualização em etapas (preview,
                  não executa). Mostra bridge→FS→final OU NO-GO se não há caminho seguro. */}
              {selRelease && (
                <StagedOtaPlanPanel deviceId={deviceId} releaseId={selRelease.id} />
              )}
              <Can role="super_admin">
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  onClick={() => doOta("Atualizar",
                    selRelease?.id)}
                  disabled={busy !== null || !selRelease || compatBlocks || (needsPhrase && !phraseOk)}
                  className="h-9 rounded-lg text-xs font-semibold
                             bg-noc-warn/10 border
                             border-noc-warn/30 text-noc-warn
                             disabled:opacity-40"
                >
                  {busy === "Atualizar" ? "…" : "Atualizar"}
                </button>
                <button
                  onClick={() => doOta("Repetir OTA",
                    selRelease?.id)}
                  disabled={busy !== null || !selRelease || compatBlocks || (needsPhrase && !phraseOk)}
                  className="h-9 rounded-lg text-xs font-semibold
                             border border-noc-border
                             text-noc-text disabled:opacity-40"
                >
                  {busy === "Repetir OTA" ? "…" : "Repetir OTA"}
                </button>
                <button
                  onClick={() => doOta("Rollback",
                    selRelease?.id)}
                  disabled={busy !== null || !selRelease || compatBlocks || (needsPhrase && !phraseOk)}
                  className="h-9 rounded-lg text-xs font-semibold
                             border border-noc-err/30
                             text-noc-err bg-noc-err/10
                             disabled:opacity-40"
                >
                  {busy === "Rollback" ? "…" : "Rollback"}
                </button>
                <button
                  disabled
                  title="Cancelar OTA não tem endpoint no backend"
                  className="h-9 rounded-lg text-xs font-semibold
                             border border-noc-border/50
                             text-noc-muted/50 cursor-not-allowed"
                >
                  Cancelar ⚠
                </button>
              </div>
              </Can>
              <div className="text-[10px] text-noc-muted/70">
                Rollback e Repetir disparam um novo ota_update
                para a versão escolhida. Cancelar não tem
                endpoint no backend.
              </div>
            </>
          )}
        </div>

        {/* 4 — PROGRESSO */}
        <div className="glass-card-pad">
          <div className="text-xs font-semibold uppercase
                          tracking-wider text-noc-muted mb-2">
            Progresso
          </div>
          <OtaProgress state={otaState} hb={hb} />
        </div>

        {/* 5 — DIAGNÓSTICO */}
        <div className="glass-card-pad space-y-1.5">
          <div className="text-xs font-semibold uppercase
                          tracking-wider text-noc-muted">
            Diagnóstico OTA
          </div>
          <TRow k="Estado OTA" v={hb.ota_state ?? "—"} />
          <TRow k="Último erro"
                v={hb.ota_last_error ?? "—"}
                tone={hb.ota_last_error ? "text-noc-err" : ""} />
          <TRow k="Última atualização"
                v={hb.ota_last_success
                  ? fmtTime(hb.ota_last_success) : "—"} />
          <TRow k="Duração"
                v={hb.ota_last_duration != null
                  ? hb.ota_last_duration + "s" : "—"} />
          <TRow k="Tentativas" v={hb.ota_attempts ?? "—"} />
          <TRow k="Falhas" v={hb.ota_failures ?? "—"} />
          <TRow k="Tempo de download"
                v={hb.ota_download_time != null
                  ? hb.ota_download_time + "s" : "—"} />
          <TRow k="Tempo de gravação"
                v={hb.ota_flash_time != null
                  ? hb.ota_flash_time + "s" : "—"} />
          <div className="text-[10px] text-noc-muted/60 pt-1">
            Campos lidos do heartbeat bruto — "—" quando o
            firmware ainda não os reporta.
          </div>
        </div>
      </div>

      {/* OTA-FS-2D — ÁREA B: atualização de FILESYSTEM (LittleFS) via o comando
          cloud fs_update (POST /fs-ota). Separada do OTA de app (U_FLASH) acima. */}
      <FsUpdateSection deviceId={deviceId} data={data} notify={notify} onAfterAction={reload} />

      {/* S7.5C-C — Eventos OTA recentes (histórico via heartbeats_raw).
          Endpoint dedicado /api/admin/devices/{id}/ota-events. Antes
          (S7.5C-B) lia hb.ota_events, mas firmware drena a fila após
          2xx, então o snapshot ficava vazio. Agora vem do histórico
          bruto persistido. */}
      <OtaEventsList deviceId={deviceId} reloadKey={reloadKey} />

      {/* 2 — HISTÓRICO OTA */}
      <div className="glass-card overflow-hidden">
        <div className="px-3 py-2 text-xs font-semibold uppercase
                        tracking-wider text-noc-muted border-b
                        border-noc-border">
          Histórico OTA
        </div>
        {otaCmds.length === 0 ? (
          <div className="px-3 py-3 text-sm text-noc-muted/70
                          italic">
            Nenhum OTA registrado para este dispositivo.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-12 gap-2 px-3 py-2
                            font-mono text-[9.5px] uppercase tracking-[0.14em]
                            text-noc-muted border-b
                            border-noc-border/50">
              <div className="col-span-3">Data</div>
              <div className="col-span-3">Versão / destino</div>
              <div className="col-span-2">Tipo</div>
              <div className="col-span-2">Resultado</div>
              <div className="col-span-2">Tempo</div>
            </div>
            {[...otaCmds]
              .sort((a, b) =>
                new Date(b.created_at) - new Date(a.created_at))
              .map((c) => (
                <div key={c.id}
                     className="grid grid-cols-12 gap-2 px-3 py-2
                                text-xs items-center border-b
                                border-noc-border/30 last:border-0">
                  <div className="col-span-3 text-noc-muted">
                    {fmtTime(c.created_at)}
                  </div>
                  <div className="col-span-3 font-mono">
                    {otaTargetVersion(c) || "—"}
                  </div>
                  <div className="col-span-2 text-noc-muted">
                    {cmdOrigin(c)}
                  </div>
                  <div className="col-span-2">
                    <Badge tone={cmdStatusTone(c.status)} title={c.status}>
                      {commandStatusLabel(c.status)}
                    </Badge>
                  </div>
                  <div className="col-span-2 text-noc-muted">
                    {cmdExecTime(c)}
                  </div>
                </div>
              ))}
          </>
        )}
      </div>

      {/* 6 — LOGS do último OTA */}
      {lastOta && (
        <div className="glass-card-pad space-y-2">
          <div className="text-xs font-semibold uppercase
                          tracking-wider text-noc-muted">
            Logs — último OTA
          </div>
          <div className="grid sm:grid-cols-2 gap-2 text-[11px]">
            <ExpRow k="Command ID" v={lastOta.id} mono />
            <ExpRow k="Status" v={lastOta.status} />
            <ExpRow k="Criado" v={fmtTime(lastOta.created_at)} />
            <ExpRow k="ACK" v={fmtTime(lastOta.acked_at)} />
          </div>
          <ExpJson label="Payload OTA" value={lastOta.payload} />
          <ExpJson label="Response / ACK" value={lastOta.result} />
        </div>
      )}
    </div>
  );
}

/**
 * NOC-OTA-COMPAT-GUARD — painel de compatibilidade da release selecionada:
 * fw atual → alvo, badge do tipo, mensagem de risco e, quando o salto pode
 * resetar config, o input da frase de confirmação. O backend é a autoridade
 * (recusa 409/412); este painel só informa e gateia o botão.
 */
function OtaCompatPanel({ compat, deviceFw, rel, resetAck, setResetAck }) {
  const badge = releaseTypeBadge(rel);
  const toneCls = {
    err: "text-noc-err bg-noc-err/10 border-noc-err/30",
    warn: "text-noc-warn bg-noc-warn/10 border-noc-warn/30",
    ok: "text-noc-ok bg-noc-ok/10 border-noc-ok/30",
    muted: "text-noc-muted bg-noc-bg border-noc-border",
  };
  const panelTone =
    compat.decision === "block" ? "err"
    : compat.decision === "needs_reset_confirm" ? "warn"
    : "ok";
  return (
    <div className={`text-[11px] rounded p-2 border space-y-1.5 ${toneCls[panelTone]}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold uppercase tracking-wider text-[10px]">
          Compatibilidade
        </span>
        <span
          className={`text-[9px] font-mono px-1 py-0.5 rounded border ${toneCls[badge.tone] || toneCls.muted}`}
          title={badge.title}
        >
          {badge.label}
        </span>
      </div>
      <div className="font-mono text-[10px]">
        <span className="text-noc-muted">atual:</span> {deviceFw || "—"}{" "}
        <span className="text-noc-muted">→ alvo:</span> {rel.version}
      </div>
      <div>{compat.message}</div>
      {compat.decision === "block" && (
        <div className="font-semibold">
          ⛔ Bloqueado — não é OTA app válido para este device.
        </div>
      )}
      {compat.decision === "needs_reset_confirm" && (
        <div className="space-y-1 pt-1">
          <div className="font-semibold">
            ⚠ Para liberar, digite exatamente a frase:
          </div>
          <div className="font-mono text-[10px]">{RESET_CONFIRM_PHRASE}</div>
          <input
            type="text"
            value={resetAck}
            onChange={(e) => setResetAck(e.target.value)}
            placeholder={RESET_CONFIRM_PHRASE}
            className="installer-input text-[11px]"
          />
        </div>
      )}
    </div>
  );
}

/**
 * OTA-FS-2D — ÁREA B da aba OTA: atualização do FILESYSTEM (LittleFS) via o
 * comando cloud fs_update (POST /fs-ota). Espelha o disparo de OTA de app, mas:
 *   - lista SÓ releases kind=fs;
 *   - capability gate por fs_ota=true (fallback fs_version = "incerto", desabilita);
 *   - strong confirm reforçando "LittleFS, não app; não reinicia";
 *   - erros 403/412 traduzidos por fsOtaErrorMessage.
 * O backend continua sendo a autoridade (403 protegido / 412 sem suporte).
 */
function FsUpdateSection({ deviceId, data, notify, onAfterAction }) {
  const confirmCritical = useCriticalConfirm();
  const [releases, setReleases] = useState([]);
  const [selRelease, setSelRelease] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  const hb = data.heartbeat || {};
  const cap = fsCapabilityState(hb);
  const isSecure = hb?.auth_diag?.secure_channel === true;
  const isProtected = getProtectionState(data).protected;

  useEffect(() => {
    let alive = true;
    listFirmwareReleases({ kind: "fs" })
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res)
          ? res
          : (res?.releases || res?.items || []);
        setReleases(list);
      })
      .catch((e) => {
        if (alive) setLoadErr(e?.message || "Erro ao carregar releases FS");
      });
    return () => { alive = false; };
  }, [deviceId]);

  // Gate final da UI: firmware suporta OTA-FS (fs_ota) E canal seguro E release
  // selecionada. Proteção é sinalizada mas quem bloqueia de fato é o backend (403).
  const canTrigger = cap.enabled && isSecure && !!selRelease && !busy;

  async function doFsUpdate() {
    if (!selRelease) {
      notify("error", "Selecione uma release de filesystem (kind=fs).");
      return;
    }
    const _secret = await confirmCritical({
      actionLabel: "Atualizar filesystem (LittleFS / OTA-FS)",
      target: { kind: "single", deviceId },
      impact:
        `Device: ${deviceId}\n` +
        `Release FS: ${selRelease.version} — arquivos LittleFS, NÃO firmware app.\n` +
        "Atualiza a interface local/arquivos LittleFS via fs_update. " +
        "NÃO reinicia o Sonoff automaticamente.",
    });
    if (_secret == null) return;
    setBusy(true);
    try {
      await triggerDeviceFsOta(deviceId, selRelease.id, { confirm: _secret });
      notify("ok", "OTA-FS enfileirado — aplica no próximo heartbeat (sem reboot).");
      setSelRelease(null);
      onAfterAction && onAfterAction();
    } catch (e) {
      notify("error", fsOtaErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="glass-card-pad space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-xs font-semibold uppercase tracking-wider text-noc-muted">
          Atualização de Filesystem / LittleFS
        </div>
        <span
          className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-noc-accent/40 text-noc-accent"
          title="Arquivo LittleFS (kind=fs), não firmware app"
        >
          FS
        </span>
        <span className="text-[11px] text-noc-muted">
          fs_version atual:{" "}
          <span className="font-mono">{cap.fsVersion || hb.fs || "—"}</span>
        </span>
      </div>

      <div
        className={
          cap.level === "supported"
            ? "text-[11px] text-noc-ok bg-noc-ok/10 border border-noc-ok/30 rounded p-2"
            : "text-[11px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2"
        }
      >
        {cap.label}
      </div>

      {!isSecure && (
        <div className="text-[11px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
          OTA-FS exige secure_channel=true (canal HMAC ativo).
        </div>
      )}

      {isProtected && (
        <div className="text-[11px] text-violet-200 bg-violet-400/10 border border-violet-400/40 rounded p-2 flex items-start gap-2">
          <span aria-hidden>🔒</span>
          <span><b>Dispositivo protegido</b> — o backend bloqueia OTA-FS (403).</span>
        </div>
      )}

      <div className="text-[11px] text-noc-muted">
        Atualiza a interface local/arquivos LittleFS.{" "}
        <b>Não reinicia o Sonoff automaticamente.</b> Requer firmware com suporte{" "}
        <span className="font-mono">fs_ota=true</span>.
      </div>

      {loadErr && (
        <div className="text-[11px] text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 break-words">
          {loadErr}
        </div>
      )}

      {releases.length === 0 ? (
        <div className="text-[12px] font-mono text-noc-faint">
          Nenhuma release de filesystem (kind=fs) disponível. Suba um{" "}
          <span className="font-mono">littlefs.bin</span> em Firmware releases.
        </div>
      ) : (
        <>
          <select
            className="installer-input"
            value={selRelease?.id || ""}
            onChange={(e) =>
              setSelRelease(releases.find((r) => r.id === e.target.value) || null)
            }
            disabled={busy}
          >
            <option value="">Selecione a release FS…</option>
            {releases.map((r) => (
              <option key={r.id} value={r.id}>
                {r.version}
                {r.deprecated ? " (obsoleta)" : ""}
              </option>
            ))}
          </select>
          {selRelease && (
            <div className="space-y-1 pt-1 text-[11px]">
              <ExpRow k="Versão FS" v={selRelease.version} />
              <ExpRow k="Tamanho" v={fmtBytes(selRelease.size_bytes)} />
              <ExpRow k="SHA-256" v={shortSha(selRelease.sha256)} mono />
              <ExpRow k="Criado em" v={fmtTime(selRelease.created_at)} />
            </div>
          )}
          <Can role="super_admin">
            <button
              onClick={doFsUpdate}
              disabled={!canTrigger}
              title={cap.enabled ? "Aplica OTA-FS (LittleFS) no device" : cap.label}
              className="h-9 w-full rounded-lg text-xs font-semibold
                         bg-noc-accent/10 border border-noc-accent/40
                         text-noc-accent disabled:opacity-40"
            >
              {busy ? "Enviando…" : "Atualizar arquivos locais / LittleFS"}
            </button>
          </Can>
        </>
      )}
    </div>
  );
}

function OtaStat({ label, value, tone }) {
  const tones = {
    ok: "text-noc-ok", err: "text-noc-err",
    warn: "text-noc-warn", info: "text-noc-accent",
    muted: "text-noc-text",
  };
  return (
    <div className="rounded-lg border border-noc-border
                    bg-noc-panel2/40 px-2.5 py-2">
      <div className="font-mono text-[9.5px] uppercase tracking-[0.14em]
                      text-noc-muted">
        {label}
      </div>
      <div className={
        "text-sm font-bold truncate "
        + (tones[tone] || "text-noc-text")
      }>
        {value}
      </div>
    </div>
  );
}

/* Progresso visual — estágios do OTA. As KEYS são as do firmware (usadas em
 * otaStageIndex/indexOf) — NÃO traduzir. O display é mapeado à parte. */
const OTA_STAGES = [
  "download", "validate", "flash", "verify", "reboot", "heartbeat",
];
const OTA_STAGE_LABELS = {
  download: "Download", validate: "Validação", flash: "Gravação",
  verify: "Verificação", reboot: "Reinício", heartbeat: "Heartbeat",
};

function OtaProgress({ state, hb }) {
  // Índice do estágio atual derivado do ota_state do heartbeat.
  const cur = otaStageIndex(state.key, hb);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Badge tone={state.tone}>{state.label}</Badge>
        {hb.ota_progress != null && (
          <span className="text-xs text-noc-muted">
            {hb.ota_progress}%
          </span>
        )}
      </div>
      <div className="space-y-1">
        {OTA_STAGES.map((stage, i) => {
          const done = cur > i;
          const active = cur === i;
          return (
            <div key={stage}
                 className="flex items-center gap-2 text-xs">
              <span className={
                "h-2 w-2 rounded-full shrink-0 "
                + (done ? "bg-noc-ok"
                   : active ? "bg-noc-accent animate-pulse"
                   : "bg-noc-panel2")
              } />
              <span className={
                done ? "text-noc-muted"
                : active ? "text-noc-accent font-medium"
                : "text-noc-muted/50"
              }>
                {OTA_STAGE_LABELS[stage] || stage}
              </span>
              {active && hb.ota_stage_time != null && (
                <span className="text-noc-muted/60 ml-auto">
                  {hb.ota_stage_time}s
                </span>
              )}
            </div>
          );
        })}
      </div>
      {state.key === "failed" && hb.ota_last_error && (
        <div className="text-[11px] text-noc-err bg-noc-err/10
                        border border-noc-err/30 rounded p-2">
          {hb.ota_last_error}
        </div>
      )}
      {state.key === "idle" && (
        <div className="text-[11px] text-noc-muted/60 italic">
          Nenhum OTA em andamento.
        </div>
      )}
    </div>
  );
}

/* Deriva o estado OTA do heartbeat + último comando. */
function otaStateOf(hb, lastOta) {
  const s = (hb.ota_state || "").toLowerCase();
  // NOC-UX — label em PT-BR (display). A `key` é comparada em código → intacta.
  const map = {
    downloading: { key: "downloading", label: "baixando", tone: "warn" },
    flashing: { key: "flashing", label: "gravando", tone: "warn" },
    verifying: { key: "verifying", label: "verificando", tone: "warn" },
    rebooting: { key: "rebooting", label: "reiniciando", tone: "warn" },
    failed: { key: "failed", label: "falhou", tone: "err" },
    success: { key: "success", label: "concluído", tone: "ok" },
  };
  if (map[s]) return map[s];
  // sem ota_state — infere do último comando
  if (lastOta) {
    if (lastOta.status === "pending"
        || lastOta.status === "delivered") {
      return { key: "downloading", label: "em andamento",
               tone: "warn" };
    }
    if (lastOta.status === "failed") {
      return { key: "failed", label: "falhou", tone: "err" };
    }
    if (lastOta.status === "acked") {
      return { key: "success", label: "concluído", tone: "ok" };
    }
  }
  return { key: "idle", label: "ocioso", tone: "muted" };
}

function otaStageIndex(stateKey, hb) {
  // Se o heartbeat reporta ota_stage, usa-o.
  if (hb.ota_stage) {
    const idx = OTA_STAGES.indexOf(
      String(hb.ota_stage).toLowerCase());
    if (idx >= 0) return idx;
  }
  const byState = {
    idle: -1, downloading: 0, flashing: 2, verifying: 3,
    rebooting: 4, success: 6, failed: -1,
  };
  return byState[stateKey] ?? -1;
}

function otaTargetVersion(c) {
  const p = c.payload || {};
  return p.version || p.target_version
    || p.release_version || (p.release_id
      ? "release " + String(p.release_id).slice(0, 8) : null);
}

function fmtBytes(n) {
  if (n == null) return "—";
  const kb = Number(n) / 1024;
  if (kb < 1024) return Math.round(kb) + " KB";
  return (kb / 1024).toFixed(1) + " MB";
}

function shortSha(s) {
  if (!s) return "—";
  return String(s).slice(0, 12) + "…";
}

function fmtAgo(v) {
  if (!v) return "—";
  const t = new Date(v).getTime();
  if (Number.isNaN(t)) return "—";
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 3600) return Math.round(s / 60) + "min";
  if (s < 86400) return Math.round(s / 3600) + "h";
  return Math.round(s / 86400) + "d";
}

/* ════════ Aba Automação (agenda) — ADV-C1 ════════ */
/**
 * Aba Automação — editor canônico de agenda do device.
 *
 * ADV-C1: converge no `ScheduleEditor` + caminho `config_schedule` (mesmo do
 * Mapa→Configurar→Agenda). NÃO autora mais `schedule_templates`. INIT-A: o
 * editor (`desired`) começa refletindo a agenda REAL resolvida do snapshot.
 * Lê: getDeviceConfigSnapshot (real) + listScheduleEvents (telemetria).
 * Envia: validateSchedule → validateScheduleForCapability →
 *   buildConfigSchedulePayload → confirmCritical → configSchedule.
 */
// S2.1 — weekdayMaskToDays e a duração padrão do pulso vêm de scheduleCore
// (fonte única). Aqui ficam só helpers de EXIBIÇÃO local da próxima execução.

// Fix 1 — Próxima execução SEM bug de timezone.
// Em vez do `next_run` do backend (hora local rotulada como UTC → −3h na UI),
// derivamos a próxima ocorrência a partir do próprio template (hour/minute/
// weekday_mask) no fuso LOCAL do navegador.
// S2.9 — a "Próxima execução" passou a derivar do snapshot do device
// (nextOccurrenceFromEntries, abaixo), não mais dos templates do banco.

// Formata a próxima ocorrência (Date LOCAL). Hora = pad2(hour):pad2(minute)
// exatamente como no template — sem nenhuma conversão de fuso.
function fmtNextLocal(d) {
  if (!d) return "—";
  const wd = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SAB"];
  const dia = wd[d.getDay()];
  const data = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  const hora = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return `${dia} • ${data} • ${hora}`;
}

// S2.9 — próxima ocorrência a partir das entries REAIS do snapshot do device
// (days[] 0=Dom..6=Sáb, alinhado a Date.getDay(); time "HH:MM"). Fonte = device.
function nextOccurrenceFromEntries(entries, now) {
  let best = null;
  for (const e of entries || []) {
    if (!e || !e.time) continue;
    const [hh, mm] = String(e.time).split(":").map(Number);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) continue;
    const days = Array.isArray(e.days) ? e.days : [];
    for (let i = 0; i < 8; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      d.setHours(hh, mm, 0, 0);
      if (!days.includes(d.getDay())) continue;
      if (d.getTime() <= now.getTime()) continue;
      if (!best || d < best) best = d;
      break;
    }
  }
  return best;
}
// S2.9 — catálogo enxuto às ações com caminho REAL no firmware/sync.
// Removidas ações FANTASMA (watchdog_reset, safe_sync, ota_window) — sem
// correspondência no firmware nem no sync (eram aceitas mas nunca executadas).
// "reboot"/"Reiniciar" → "relay_reboot"/"Reiniciar Relé" (id correto do pulso
// de relé; evita confusão com reboot do ESP32).
const SCHED_ACTIONS = [
  { id: "relay_on", label: "Relé LIGADO" },
  { id: "relay_off", label: "Relé DESLIGADO" },
  { id: "relay_reboot", label: "Reiniciar Relé" },
];

// NOC-UX — display PT-BR de tipo/status de COMANDO. Só renderização: o valor cru
// (relay_on, pending, …) continua nas comparações de código e no title/tooltip.
const COMMAND_TYPE_LABELS = {
  relay_on: "Relé LIGADO", relay_off: "Relé DESLIGADO",
  relay_reboot: "Reiniciar Relé", reboot: "Reiniciar ESP", ping: "Ping",
  config_watchdog: "Config Watchdog", config_schedule: "Config Agenda",
  config_management: "Config Gestão", config_wifi: "Config WiFi",
  config_network: "Config Rede", config_advanced: "Config Avançada",
  ota_update: "OTA", trigger_ota: "OTA",
};
const commandTypeLabel = (t) => COMMAND_TYPE_LABELS[t] || t || "—";
const COMMAND_STATUS_LABELS = {
  pending: "Pendente", delivered: "Entregue", acked: "Confirmado",
  failed: "Falhou", expired: "Expirado",
};
const commandStatusLabel = (s) => COMMAND_STATUS_LABELS[s] || s || "—";

// S3.10B — ações de agenda que derrubam serviço → exigem confirmação por senha.
const CRITICAL_SCHED_ACTIONS = new Set(["relay_off", "relay_reboot", "esp_reboot"]);
const isCriticalSched = (a) => CRITICAL_SCHED_ACTIONS.has(a);

function AutomationTab({ deviceId, data, notify }) {
  const confirmCritical = useCriticalConfirm(); // S3.10B
  // ADV-C1 — desejado da aba = config_schedule (mesmo modelo do ScheduleEditor
  // do Mapa). Substitui o ScheduleForm/schedule_templates. INIT-A: o editor
  // começa refletindo a agenda REAL resolvida do device (ver useEffect).
  const [desired, setDesired] = useState({ enabled: false, entries: [], exceptions: [] });
  const [executions, setExecutions] = useState([]);
  // S2.6/S2.9 — bloco schedule_v2 (persistido) para observabilidade.
  const [realScheduleV2, setRealScheduleV2] = useState(null);
  // S2.9 — snapshot completo do device (fonte da verdade) + flag de presença.
  const [realSnapshot, setRealSnapshot] = useState(null);
  const [realSyncedAt, setRealSyncedAt] = useState(null);
  const [snapshotKnown, setSnapshotKnown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);

  const hb = data.heartbeat || {};
  const onEditorChange = (patch) => setDesired((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.allSettled([
      // S2.9/B1 — fonte REAL de execuções = telemetria do firmware
      // (schedule-events), não a tabela schedule_executions (nunca gravada).
      listScheduleEvents(deviceId, { limit: 30 }),
      getDeviceConfigSnapshot(deviceId),
    ]).then(([exe, snap]) => {
      if (!alive) return;
      if (exe.status === "fulfilled") {
        setExecutions(exe.value?.events || []);
      }
      if (snap.status === "fulfilled") {
        const ss = snap.value?.last_config_snapshot || null;
        setRealScheduleV2(ss?.schedule_v2 || null);
        setRealSnapshot(ss);
        setRealSyncedAt(snap.value?.config_synced_at || null);
        setSnapshotKnown(true);
        // INIT-A — o editor começa refletindo a agenda REAL resolvida do device
        // (v1 OU v2). Editar diverge; "Enviar" sobrescreve via config_schedule.
        const real = resolveActiveSchedule(ss, data).active.entries || [];
        setDesired({
          enabled: real.length > 0,
          entries: real.map((e) => ({
            days: Array.isArray(e.days) ? [...e.days] : [],
            time: e.time,
            duration_s: e.duration_s != null ? e.duration_s : SCHEDULE_PULSE_DEFAULT_S,
            action: e.action || "relay_reboot",
            enabled: true,
          })),
          exceptions: Array.isArray(ss?.schedule?.exceptions) ? [...ss.schedule.exceptions] : [],
        });
      } else {
        setRealScheduleV2(null);
        setRealSnapshot(null);
        setRealSyncedAt(null);
        setSnapshotKnown(false);
        setDesired({ enabled: false, entries: [], exceptions: [] });
      }
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, reloadKey]);

  function reload() { setReloadKey((k) => k + 1); }

  // ADV-C1 — desejado = entries do editor (config_schedule). "Eventos no
  // editor" conta entries habilitadas do editor.
  const desiredEntries = Array.isArray(desired.entries) ? desired.entries : [];
  const desiredEnabledCount = desiredEntries.filter((e) => e.enabled !== false).length;
  // S2.9 — FONTE DA VERDADE: device/snapshot. A agenda ATIVA e a "Próxima
  // execução" derivam do snapshot do device.
  const _now = new Date();
  const activeSched = resolveActiveSchedule(realSnapshot, data);
  // Próxima execução = próxima ocorrência das entries REAIS (days[] 0=Dom + time).
  const nextWhen = snapshotKnown
    ? nextOccurrenceFromEntries(activeSched.active.entries, _now)
    : null;
  // S2.1/ADV-E — capability do firmware (scheduleCore). `actionAware` liga a
  // comparação por ação SÓ p/ v2/multi-event; v1 (reboot-only) fica no legado.
  const schedCapability = getSchedulerCapability(data);
  const scheduleActionAware = schedCapability.multi_event === true;
  // ADV-C1 — divergência DESEJADO (editor/config_schedule) × REAL (snapshot).
  // Adapter local (forma de template) p/ reusar compareScheduleSync SEM tocar
  // scheduleCore. Duração fora da chave (ADV-C2).
  const _entryToTemplateShape = (e) => {
    const [hh, mm] = String(e.time || e.on_time || "").split(":");
    return {
      hour: Number(hh) || 0,
      minute: Number(mm) || 0,
      weekday_mask: daysToWeekdayMask(e.days),
      action: e.action,
      enabled: e.enabled !== false,
    };
  };
  const syncCmp = compareScheduleSync(
    desiredEntries.map(_entryToTemplateShape),
    activeSched.active.entries,
    { actionAware: scheduleActionAware },
  );
  // "Última execução" — fonte confiável é a telemetria REAL do firmware.
  const lastExec = [...executions]
    .filter((e) => e.executed_at || e.created_at)
    .sort((a, b) =>
      new Date(b.executed_at || b.created_at) - new Date(a.executed_at || a.created_at))[0]
    || null;

  // Conflito = a regra v1/v2 única reprova os eventos desejados atuais.
  const scheduleConflict = !validateScheduleForCapability(desiredEntries, schedCapability).ok;

  // ADV-C1 — envia o DESEJADO do editor (config_schedule); porta o submit do
  // ScheduleTab do Mapa. Sem schedule_templates / syncSchedule.
  async function submit() {
    const vErr = validateSchedule(desired);
    if (vErr) { notify("error", vErr); return; }
    const vc = validateScheduleForCapability(desired.entries, schedCapability);
    if (!vc.ok) { notify("error", vc.reason); return; }
    const payload = buildConfigSchedulePayload({
      entries: desired.entries,
      exceptions: desired.exceptions,
      enabled: desired.enabled,
    });
    // UX-C — divergência editor × dispositivo: aviso forte de SOBRESCRITA antes do envio.
    const divergent = snapshotKnown && syncCmp.state === "divergent";
    let confirm;
    if ((payload.entries || []).some((e) => isCriticalSched(e.action)) || divergent) {
      const secret = await confirmCritical({
        ...(divergent ? { title: "Sobrescrever a agenda do dispositivo?" } : {}),
        actionLabel: "Enviar agenda ao dispositivo",
        target: { kind: "single", deviceId },
        impact: divergent ? (
          <div className="space-y-1.5">
            <div className="text-noc-err font-semibold">⚠ Editor difere do dispositivo.</div>
            <div>
              Enviar a agenda ao dispositivo vai <b>SOBRESCREVER</b> a agenda
              local atual do Sonoff. O rascunho do editor não fica salvo no NOC.
            </div>
            {scheduleSummaryNode("Será enviado (editor)", payload.entries)}
            {scheduleSummaryNode("Será sobrescrito (dispositivo)", activeSched.active.entries)}
          </div>
        ) : "Esta ação vai PROGRAMAR o dispositivo físico (pulsos de reinício de relé).",
      });
      if (secret == null) return;
      confirm = secret;
    }
    setBusy(true);
    try {
      await configSchedule(deviceId, payload, { confirm });
      notify("ok", `Enviado ao dispositivo — ${payload.entries.length} evento(s).`);
      reload();
    } catch (e) {
      notify("error", `Envio falhou: ${e?.message || "erro"}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* S2.9 — Agenda REAL ATIVA do dispositivo (v1 OU v2 resolvido do
          snapshot). SOMENTE LEITURA. Fonte = device, nunca o banco. */}
      <RealDeviceScheduleCard
        entries={activeSched.active.entries}
        source={activeSched.activeSource}
      />

      {/* Stopgap — snapshot atrasado: heartbeat já indica agenda ativa, mas a
          LISTA de eventos (snapshot) ainda não chegou (edição local no Sonoff
          não bumpa configRevision → ressincroniza só a cada ~10 hb). */}
      {activeSched.active.entries.length === 0 && activeSched.liveness?.active && (
        <div className="flex items-start gap-2 text-xs rounded-lg border
                        border-noc-accent/30 bg-noc-accent/10 text-noc-accent px-3 py-2">
          <span>🗓</span>
          <span>
            <b>Agenda ativa detectada pelo heartbeat</b> — aguardando o snapshot da
            lista de eventos (pode levar até ~10 min após editar no Sonoff).
            {activeSched.liveness.nextRun > 0 &&
              <> · Próxima: {fmtScheduleEpoch(activeSched.liveness.nextRun)}</>}
            {lastExec &&
              <> · Última: {fmtTime(lastExec.executed_at || lastExec.created_at)}</>}
            {realSyncedAt &&
              <> · snapshot sincronizado em {fmtTime(realSyncedAt)}</>}
          </span>
        </div>
      )}

      {/* S2.9/ADV-C1 — Sincronização EDITOR (rascunho) × REAL (device). Informativo. */}
      {snapshotKnown && (desiredEntries.length > 0 || activeSched.active.entries.length > 0) && (
        <div className="space-y-1">
          <div className={"ipwd-sync-badge is-" + syncCmp.state}>
            {syncCmp.state === "synced"
              ? "✓ Editor igual ao dispositivo"
              : syncCmp.state === "pending"
                ? "⏳ Pendente — agenda do editor ainda não enviada ao dispositivo"
                : "⚠ Editor difere do dispositivo"}
          </div>
          {syncCmp.state === "divergent" && (
            <div className="text-xs text-noc-muted px-1">
              O editor contém uma agenda diferente da agenda real do Sonoff
              (edição ainda não enviada). Enviar sobrescreve a agenda local.
            </div>
          )}
        </div>
      )}

      {/* S2.6 — Agenda V2 persistida (badge dinâmico: dormente/executando). */}
      <ScheduleV2Card snapshot={realScheduleV2} />

      {/* Cards de status */}
      <div className="grid grid-cols-2 sm:grid-cols-3
                      lg:grid-cols-6 gap-2">
        <AutoStat label="Próxima execução"
                  value={!snapshotKnown
                    ? "Aguardando snapshot do dispositivo"
                    : (nextWhen ? fmtNextLocal(nextWhen)
                       : (activeSched.liveness?.nextRun > 0
                          ? fmtScheduleEpoch(activeSched.liveness.nextRun)
                          : "não disponível"))}
                  hint={snapshotKnown
                    ? (nextWhen
                       ? `fonte: device (${activeSched.activeSource})`
                       : (activeSched.liveness?.nextRun > 0
                          ? "fonte: heartbeat (snapshot pendente)" : null))
                    : null}
                  tone={snapshotKnown ? "info" : "muted"}
                  small={!snapshotKnown || (!nextWhen && !(activeSched.liveness?.nextRun > 0))} />
        <AutoStat label="Última execução"
                  value={lastExec
                    ? fmtTime(lastExec.executed_at || lastExec.created_at)
                    : "não disponível"}
                  hint={lastExec
                    ? (actionLabel(lastExec.action) || lastExec.result)
                    : null}
                  tone={lastExec ? undefined : "muted"}
                  small={!lastExec} />
        <AutoStat label="RTC"
                  value={hb.rtc_ok ? "OK"
                    : "aguardando firmware"}
                  tone={hb.rtc_ok ? "ok" : "muted"}
                  small={!hb.rtc_ok} />
        <AutoStat label="NTP"
                  value={hb.ntp_ok ? "OK"
                    : "aguardando firmware"}
                  tone={hb.ntp_ok ? "ok" : "muted"}
                  small={!hb.ntp_ok} />
        <AutoStat label="Editor × dispositivo"
                  value={!snapshotKnown ? "Aguardando dispositivo"
                    : syncCmp.state === "synced" ? "Igual ao dispositivo"
                    : syncCmp.state === "divergent" ? "Divergente"
                    : "Pendente"}
                  tone={!snapshotKnown ? "muted"
                    : syncCmp.state === "synced" ? "ok"
                    : syncCmp.state === "divergent" ? "warn"
                    : "info"}
                  small={!snapshotKnown || syncCmp.state === "synced"}
                  hint="Compara a agenda do editor com a agenda real do dispositivo. O rascunho do editor não fica salvo no NOC." />
        <AutoStat label="Eventos no editor"
                  value={desiredEnabledCount} tone="info"
                  hint="Eventos habilitados no editor, não necessariamente o que está rodando no dispositivo." />
        <AutoStat label="Eventos no dispositivo"
                  value={snapshotKnown ? activeSched.active.entries.length : "—"}
                  tone={snapshotKnown ? "info" : "muted"}
                  hint={snapshotKnown
                    ? "Eventos na agenda real do dispositivo"
                    : "Aguardando snapshot do dispositivo"} />
      </div>

      {/* wsv1 single-event — aviso de limitação quando há >1 evento ativo */}
      {scheduleConflict && (
        <div className="flex items-start gap-2 text-xs rounded-lg border
                        border-noc-warn/40 bg-noc-warn/10 text-noc-warn
                        px-3 py-2">
          <span>⚠</span>
          <span>
            Firmware atual suporta apenas <b>1 agenda ativa</b>. Há{" "}
            {desiredEnabledCount} eventos habilitados no editor — desative os
            outros antes de enviar (o envio está bloqueado).
          </span>
        </div>
      )}

      {/* Barra de status — chip de capability REAL do scheduler (visível a todos) */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={
          "inline-flex items-center gap-1 text-[10px] font-semibold "
          + "px-2 py-1.5 rounded-lg border "
          + (schedCapability.multi_event
              ? "border-noc-ok/40 text-noc-ok bg-noc-ok/10"
              : "border-noc-border text-noc-muted bg-noc-panel2/30")
        }
          title={`Fonte: ${schedCapability.source}`}>
          🗓 Scheduler v{schedCapability.version}{" "}
          {schedCapability.multi_event
            ? `multi-event (até ${schedCapability.max_entries})`
            : "single-event"}
        </span>
      </div>

      {/* ADV-C1 — Editor avançado canônico (config_schedule). Autoria = super_admin.
          INIT-A: começa refletindo a agenda real do device. */}
      <Can role="super_admin">
        <div className="glass-card-pad space-y-2">
          <div>
            <div className="text-xs font-semibold uppercase
                            tracking-wider text-noc-muted">
              Editor de agenda (rascunho baseado no dispositivo)
            </div>
            <div className="text-[11px] text-noc-muted/70 mt-0.5">
              Começa refletindo a agenda real do dispositivo. Edite e use
              “Enviar agenda ao dispositivo” para gravar no Sonoff (sobrescreve
              a agenda local). O rascunho não fica salvo no NOC — ao recarregar,
              o editor volta a refletir o dispositivo.
              {!schedCapability.multi_event && (
                <> Ligar/desligar agendado depende de firmware v2 (ADV-D); este
                dispositivo executa pulso de reinício.</>
              )}
            </div>
          </div>
          {loading ? (
            <div className="text-sm font-mono text-[12px] text-noc-faint">
              Carregando agenda…
            </div>
          ) : (
            <ScheduleEditor
              data={desired}
              onChange={onEditorChange}
              showExceptions
              // ACT-2-NOC — liga/desliga discreto só com scheduler v2 no device
              allowSwitchActions={schedCapability.multi_event === true}
            />
          )}
          <div className="flex justify-end">
            <button
              onClick={submit}
              disabled={busy || scheduleConflict}
              title={scheduleConflict
                ? "Desative os outros eventos: o firmware v1 só aceita 1 agenda ativa"
                : "Envia a agenda do editor para o dispositivo e pode sobrescrever a agenda local do Sonoff."}
              className="h-9 px-3 rounded-lg text-sm font-semibold
                         bg-noc-accent/15 text-noc-accent border
                         border-noc-accent/40 disabled:opacity-40
                         disabled:cursor-not-allowed
                         transition-colors"
            >
              {busy ? "…" : "↻ Enviar agenda ao dispositivo"}
            </button>
          </div>
        </div>
      </Can>

      {/* Execuções */}
      <div className="glass-card-pad">
        <div className="text-xs font-semibold uppercase
                        tracking-wider text-noc-muted mb-2">
          Execuções
        </div>
        {executions.length === 0 ? (
          <EmptyState
            icon="⏳"
            title="Nenhuma execução registrada ainda"
            text={"As execuções reais do firmware (relay_reboot etc.) "
              + "aparecem aqui assim que ocorrerem."}
          />
        ) : (
          <div className="space-y-1">
            {executions.map((e) => (
              <div key={e.id}
                   className="flex gap-3 text-xs items-center
                              border-l-2 pl-3 py-1.5"
                   style={{
                     borderColor: execColor(e.result),
                   }}>
                <span className="text-noc-muted/70 shrink-0 w-28">
                  {fmtTime(e.executed_at || e.created_at)}
                </span>
                <span className="text-noc-muted/60 shrink-0 w-10 font-mono"
                      title="Slot da agenda (entry_idx)">
                  {e.entry_idx != null ? `#${e.entry_idx}` : "—"}
                </span>
                <span className="flex-1 font-mono">
                  {actionLabel(e.action) || "—"}
                  {e.duration_s != null && (
                    <span className="text-noc-muted/60"> · {e.duration_s}s</span>
                  )}
                </span>
                <Badge tone={execTone(e.result)}>
                  {e.result}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* Fase 2 — Agenda REAL do dispositivo (scheduler v1 do firmware).
   SOMENTE LEITURA. Lê config_snapshot.schedule e mostra apenas as
   entries com source==="firmware_v1" — igual ao card do modal do mapa
   (DeviceConfigModal). Não há edição, sync ou escrita aqui. */
const _SCHED_DOW = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

// UX-C — resumo curto (DISPLAY-ONLY) p/ o modal de confirmação na divergência.
// Reusa _SCHED_DOW + actionLabel; entries já trazem `time` "HH:MM". Cap 4 + "… +N".
function scheduleSummaryNode(titulo, lista) {
  const arr = Array.isArray(lista) ? lista : [];
  const fmt = (e) =>
    `${e.time || "--:--"} · ${(e.days || []).map((d) => _SCHED_DOW[d]).join(",") || "—"}`
    + ` · ${e.duration_s != null ? e.duration_s + "s" : "—"} · ${actionLabel(e.action)}`;
  return (
    <div className="mt-1">
      <div className="font-semibold">{titulo} — {arr.length} evento(s):</div>
      {arr.slice(0, 4).map((e, i) => (
        <div key={i} className="font-mono text-[11px]">• {fmt(e)}</div>
      ))}
      {arr.length > 4 && <div className="text-[11px]">… +{arr.length - 4}</div>}
    </div>
  );
}

function RealDeviceScheduleCard({ entries, source }) {
  // S2.9 — recebe a agenda ATIVA já resolvida (v1 OU v2) do snapshot do device.
  const list = entries || [];
  if (list.length === 0) return null;
  return (
    <div className="space-y-1 border border-noc-warn/30
                    bg-noc-warn/10 rounded p-2">
      <div className="text-[11px] text-noc-warn flex
                      items-center gap-1.5">
        <span>🔒</span>
        <span>Agenda real NO dispositivo</span>
        <span className="ipwd-exec-source">Execução: {source || "v1"}</span>
      </div>
      <div className="text-[10px] text-noc-warn">
        Lida do snapshot/heartbeat do Sonoff.
      </div>
      {list.map((e, i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-2 text-xs
                     bg-noc-bg/40 border border-noc-border/60
                     rounded px-2 py-1"
        >
          <span className="font-mono text-noc-accent">
            {e.time || "--:--"}
          </span>
          <span className="text-noc-muted">
            {(e.days || []).map((d) => _SCHED_DOW[d]).join(", ") || "—"}
          </span>
          <span className="text-noc-muted">
            {e.duration_s != null ? `${e.duration_s}s` : "—"}
          </span>
          <span className="font-mono text-noc-warn">
            {e.action || "relay_reboot"}
          </span>
        </div>
      ))}
    </div>
  );
}

/* Card de stat — agora aceita hint (linha extra) e small. */
function AutoStat({ label, value, tone, hint, small }) {
  const tones = {
    ok: "text-noc-ok", err: "text-noc-err",
    warn: "text-noc-warn", info: "text-noc-accent",
    muted: "text-noc-muted",
  };
  return (
    <div className="rounded-lg border border-noc-border
                    bg-noc-panel2/40 px-2.5 py-2">
      <div className="font-mono text-[9.5px] uppercase tracking-[0.14em]
                      text-noc-muted">
        {label}
      </div>
      <div className={
        (small ? "text-[11px] leading-tight " : "text-sm ")
        + "font-bold truncate "
        + (tones[tone] || "text-noc-text")
      }>
        {value}
      </div>
      {hint && (
        <div className="text-[10px] text-noc-muted truncate"
             title={typeof hint === "string" ? hint : undefined}>
          {hint}
        </div>
      )}
    </div>
  );
}

/* Estado vazio elegante. */
function EmptyState({ icon, title, text }) {
  return (
    <div className="flex flex-col items-center justify-center
                    text-center py-8 px-4">
      <div className="text-4xl opacity-30 mb-2">{icon}</div>
      <div className="text-sm font-medium text-noc-text mb-1">
        {title}
      </div>
      <div className="text-xs text-noc-muted/70 max-w-sm
                      leading-relaxed">
        {text}
      </div>
    </div>
  );
}

function actionLabel(a) {
  // S2.9 — alias legado: "reboot" (agenda antiga) exibe como "Reiniciar Relé".
  if (a === "reboot") return "Reiniciar Relé";
  const f = SCHED_ACTIONS.find((x) => x.id === a);
  return f ? f.label : (a || "—");
}

function pad2(n) {
  return String(n ?? 0).padStart(2, "0");
}

function execTone(r) {
  // S2.9/B1 — vocabulário real do firmware (schedule-events): ok/fail/skipped/busy.
  // Mantém aliases legados (executed/failed) por compatibilidade.
  if (r === "ok" || r === "executed") return "ok";
  if (r === "fail" || r === "failed") return "err";
  if (r === "skipped") return "warn";
  if (r === "busy") return "muted";
  return "muted";
}

function execColor(r) {
  if (r === "ok" || r === "executed") return "#00c47f";
  if (r === "fail" || r === "failed") return "#f94144";
  if (r === "skipped") return "#f5a400";
  if (r === "busy") return "#3b82a6";
  return "#1c3650";
}

/* ════════ Auxiliares ════════ */
function Badge({ tone, children, title }) {
  const tones = {
    ok: "text-noc-ok bg-noc-ok/10 border-noc-ok/30",
    err: "text-noc-err bg-noc-err/10 border-noc-err/30",
    warn: "text-noc-warn bg-noc-warn/10 border-noc-warn/30",
    muted: "text-noc-muted bg-noc-panel2 border-noc-border",
  };
  return (
    <span title={title} className={
      "text-[10px] font-semibold rounded px-1.5 py-0.5 border "
      + (tones[tone] || tones.muted)
    }>
      {children}
    </span>
  );
}

function isOnline(lastSeen) {
  if (!lastSeen) return false;
  const t = new Date(lastSeen).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < 5 * 60 * 1000;
}

function fmtTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtUptime(s) {
  if (s == null) return "—";
  const n = Number(s);
  if (Number.isNaN(n)) return "—";
  const h = Math.floor(n / 3600);
  if (h < 24) return h + "h";
  return Math.floor(h / 24) + "d " + (h % 24) + "h";
}

// Etapa 4B — dias da semana compactos p/ o card "// AGENDAMENTO"
// (days[] 0=Dom..6=Sáb, mesmo eixo de Date.getDay/scheduleCore).
const DAY_SHORT = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
function fmtDaysShort(days) {
  if (!Array.isArray(days) || days.length === 0) return "";
  if (days.length === 7) return "todos";
  return days.map((d) => DAY_SHORT[d] ?? d).join(" ");
}

function healthTone(s) {
  if (s === "healthy" || s === "ok") return "ok";
  if (s === "warning" || s === "degraded") return "warn";
  if (s === "critical" || s === "offline") return "err";
  return "muted";
}

function sevTone(s) {
  if (s === "critical") return "err";
  if (s === "warning") return "warn";
  return "muted";
}

function rssiTone(rssi) {
  if (rssi == null) return "";
  if (rssi >= -65) return "text-noc-ok";
  if (rssi >= -75) return "text-noc-warn";
  return "text-noc-err";
}

function rssiPct(rssi) {
  // -50 dBm ≈ 100%, -90 dBm ≈ 0%.
  if (rssi == null) return 0;
  return Math.max(0, Math.min(100, ((rssi + 90) / 40) * 100));
}

function countPct(n, max) {
  if (n == null) return 0;
  return Math.max(0, Math.min(100, (Number(n) / max) * 100));
}

function countOtaFails(data) {
  return (data.commands || []).filter(
    (c) => c.type === "ota_update" && c.status === "failed"
  ).length;
}
